<template>
  <div class="overflow-y-visible" :class="{ 'mb-5': !isEmbedMode }">
    <div
      class="diagram-container position-relative"
      v-on:keydown.stop
    >
      <transition name="slide-in">
        <bpmn-side-panel
          ref="sidePanel"
          v-show="showSidePanel"
          :node="eventsById[latestEventId]"
          :types-to-display-in-diagram="cardTypes"
          :cards="cardsByEventId[latestEventId]"
          :show-done="showDone"
          @add-card-ai="addCardAI"
          @ask-for-reply="onAskForReply"
          @update-card="onUpdateCard"
        />
      </transition>
      <template v-if="(!events || events?.length === 0) && !$store.state.isWorkflowDisabled && !creatingEvent">
        <div class="d-flex flex-row" :style="`position: absolute; z-index: 1; pointer-events: none; top: ${diagramYOffset}px;`">
          <v-btn
            variant="outlined"
            size="large"
            @click="addShape({parents: ['start']})"
            class="create-first-event-button ml-15 mt-14 mb-15"
            prepend-icon="mds:add-circle"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Add start point
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            class="generate-button ml-10 mt-14 mb-15 mr-10"
            @click="openAiGenerator()"
            prepend-icon="mds:temp_preferences_custom"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Generate workflow with AI
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            class="generate-button mt-14 mb-15 mr-5"
            @click="showMcpDialog = true"
            prepend-icon="custom:McpIcon"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Start with MCP
          </v-btn>
        </div>
      </template>
      <div ref="canvas" class="canvas-container">
        <div v-show="placingGroup" class="place-group-hint">
          <v-icon size="small" class="mr-1">mds:add</v-icon>
          Click where the new group should start — press Esc to cancel
        </div>
        <div ref="scrollContainer" class="scroll-container" :class="{ 'placing-group': placingGroup }" :data-interaction-mode="$store.state.interactionMode" :data-connection-dragging="(connectionDrag || connectionEdit) ? 'true' : null">
          <svg
            v-show="events"
            :width="isEmbedMode ? diagramWidth * embedZoom : diagramWidth"
            :height="isEmbedMode ? flowChartHeight * embedZoom : flowChartHeight"
            :viewBox="isEmbedMode ? `0 0 ${diagramWidth} ${flowChartHeight}` : undefined"
            id="flowchart"
            ref="flowchart"
          >
            <foreignObject
              v-if="Object.keys(svgGroups).length > 0"
              :x="0"
              :y="0"
              :width="LANE_WIDTH"
              :height="GROUP_HEIGHT"
              class="group group-first"
            />
            <g v-for="(group) in svgGroups" :key="group.id">
              <foreignObject
                :key="group.id"
                :x="getXPointFromGroup(group)"
                :y="0"
                :width="group.width > 0 ? group.width : (diagramWidth - getXPointFromGroup(group))"
                height="24"
              >
                <div
                  style="position: relative;"
                  class="hover-container"
                  :class="{
                    'group-header-draggable': editingGroupId !== group.id && !$store.state.isWorkflowDisabled,
                    'group-header-editing': editingGroupId === group.id
                  }"
                  :data-test="group.name"
                  @mousedown="onGroupHeaderMouseDown($event, group)"
                  @mouseenter="measureVisibleBoardRight()"
                >
                  <div
                    :ref="`groupName-${group.id}`"
                    :class="{
                      'group-last': group.width === -1
                    }"
                    class="group single-line d-flex align-center justify-center"
                    :id="group.id"
                    @keydown.stop="checkCharacterLimit($event, 40)"
                    @keydown.stop.enter="$event.target.blur()"
                    @keydown.stop.esc="restoreValue($event, group.name)"
                    @blur="onGroupNameBlur($event, group)"
                    @paste="onPaste($event, $refs[`groupName-${group.id}`][0], 40, true)"
                    @click="closeInspectEvent()"
                    :contenteditable="editingGroupId === group.id && !$store.state.isWorkflowDisabled"
                    v-text="group.name"
                    @input="() => updateFontSizeDynamically($refs[`groupName-${group.id}`][0], true)"
                  >
                  </div>
                  <v-tooltip
                    location="top"
                    open-delay="500"
                    v-if="!$store.state.isWorkflowDisabled && adjacentGroup(group, 'left')"
                  >
                    <template #activator="{ props: tooltipProps }">
                      <div
                        class="move-group-left-button"
                        data-test="switch-group-left-button"
                        @mousedown.stop
                        @click="switchGroup(group, 'left')"
                      >
                        <button class="captures-description">
                          <v-icon v-bind="tooltipProps">mdi-chevron-left</v-icon>
                        </button>
                      </div>
                    </template>
                    <span>Move the group to the left</span>
                  </v-tooltip>
                  <v-tooltip
                    location="top"
                    open-delay="500"
                    v-if="!$store.state.isWorkflowDisabled && adjacentGroup(group, 'right')"
                  >
                    <template #activator="{ props: tooltipProps }">
                      <div
                        class="move-group-right-button"
                        data-test="switch-group-right-button"
                        @mousedown.stop
                        @click="switchGroup(group, 'right')"
                      >
                        <button class="captures-description">
                          <v-icon v-bind="tooltipProps">mdi-chevron-right</v-icon>
                        </button>
                      </div>
                    </template>
                    <span>Move the group to the right</span>
                  </v-tooltip>
                  <v-menu
                    v-if="!$store.state.isWorkflowDisabled"
                    location="bottom end"
                  >
                    <template #activator="{ props: menuProps }">
                      <div
                        class="group-actions-button"
                        :style="groupActionsStyle(group)"
                        data-test="group-actions-button"
                        @mousedown.stop
                        v-bind="menuProps"
                      >
                        <button class="captures-description">
                          <v-icon class="group-actions-icon">mdi-dots-vertical</v-icon>
                        </button>
                      </div>
                    </template>
                    <v-list density="compact">
                      <v-list-item
                        data-test="add-group-before-menu-item"
                        prepend-icon="mdi-arrow-left"
                        @click="addIntermediateGroup(group, 'before')"
                      >
                        <v-list-item-title>Add group before</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        data-test="add-group-after-menu-item"
                        prepend-icon="mdi-arrow-right"
                        @click="addIntermediateGroup(group, 'after')"
                      >
                        <v-list-item-title>Add group after</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        data-test="ungroup-menu-item"
                        prepend-icon="mdi-ungroup"
                        @click="ungroup(group)"
                      >
                        <v-list-item-title>Ungroup</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        base-color="error"
                        data-test="delete-group-menu-item"
                        prepend-icon="mds:delete"
                        @click="deleteGroup(group)"
                      >
                        <v-list-item-title>Delete group</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </foreignObject>
            </g>

            <!-- Group drag-reorder overlay: a ghost of the dragged group column (below the
                 header) following the cursor. -->
            <g v-if="groupReorder.active" data-test="group-reorder-ghost" style="pointer-events: none;">
              <rect
                :x="groupReorder.ghostLeft"
                :y="GROUP_HEIGHT"
                :width="groupReorder.ghostWidth"
                :height="flowChartHeight - GROUP_HEIGHT"
                :style="{ fill: 'var(--diagram-group-header)' }"
                fill-opacity="0.4"
                stroke="none"
              />
              <line
                :x1="groupReorder.ghostLeft"
                :y1="GROUP_HEIGHT"
                :x2="groupReorder.ghostLeft"
                :y2="flowChartHeight"
                stroke="rgb(46, 156, 230)"
                stroke-width="2"
                stroke-dasharray="6 4"
              />
              <line
                :x1="groupReorder.ghostLeft + groupReorder.ghostWidth"
                :y1="GROUP_HEIGHT"
                :x2="groupReorder.ghostLeft + groupReorder.ghostWidth"
                :y2="flowChartHeight"
                stroke="rgb(46, 156, 230)"
                stroke-width="2"
                stroke-dasharray="6 4"
              />
              <text
                :x="groupReorder.ghostLeft + 8"
                :y="GROUP_HEIGHT + 18"
                fill="var(--diagram-stroke)"
                font-size="14"
                font-weight="600"
              >{{ groupReorder.draggedName }}</text>
            </g>

            <svg
              :y="diagramYOffset"
              :width="diagramWidth"
              :height="flowChartHeight"
              id="svg"
              ref="innerSvg"
            >
              <rect
                v-if="!hasGroups"
                :x="4"
                :y="0.5"
                :width="diagramWidth + 1"
                :height="flowChartHeight - 1"
                fill="none"
                :style="{ stroke: 'var(--diagram-border)' }"
                class="main-rect"
              />
              <g
                v-for="(lane, name, index) in svgLanes"
                :key="'swimlane-' + lane.id"
                class="swime-lane-path"
                @click="closeInspectEvent()"
              >
                <path
                  :d="
                    computeSwimlanePath(
                      lane.x + 24,
                      lane.y,
                      diagramWidth,
                      lane.height,
                      index === 0
                    )
                  "
                  style="stroke: var(--diagram-lane-divider); stroke-width: 1px"
                ></path>
                <rect
                  :x="lane.x"
                  :y="lane.y"
                  :width="diagramWidth"
                  :height="lane.height"
                  :opacity="(index % 2 === 0) ? 0.0 : 0.01"
                  fill="black"
                  class="lane-rect"
                  :data-test="`lane-rect-${lane.name}`"
                  @contextmenu.prevent="showLaneContextMenu($event, lane, index)"
                />
                <template v-if="hasGroups">
                  <g
                    v-for="group in sortedGroups"
                    :key="`lane-cell-${lane.id}-${group.id}`"
                    class="lane-cell-group"
                  >
                    <rect
                      :x="getXPointFromGroup(group)"
                      :y="lane.y"
                      :width="getGroupWidth(group)"
                      :height="lane.height"
                      fill="transparent"
                      class="lane-rect lane-cell-hit"
                      @contextmenu.prevent="showLaneContextMenu($event, lane, index, group)"
                    />
                    <foreignObject
                      :x="getXPointFromGroup(group) + LANE_CELL_MENU_OFFSET_X"
                      :y="lane.y + LANE_CELL_MENU_OFFSET_Y"
                      :width="LANE_CELL_MENU_SIZE"
                      :height="LANE_CELL_MENU_SIZE"
                      class="lane-cell-menu-container"
                    >
                      <div v-if="!isEmbedMode" class="lane-cell-menu-wrapper">
                        <button
                          type="button"
                          :id="`lane-menu-button-${lane.name.split(' ').join('-')}`"
                          :data-test="`lane-menu-button-${lane.name.split(' ').join('-')}-${group.name.split(' ').join('-')}`"
                          class="lane-cell-menu-button"
                          :disabled="$store.state.isWorkflowDisabled"
                          @click.stop="showLaneCellMenu($event, lane, index, group)"
                        >
                          <v-icon size="16">mdi-dots-horizontal</v-icon>
                        </button>
                      </div>
                    </foreignObject>
                  </g>
                </template>
                <template v-else>
                  <g class="lane-cell-group">
                    <rect
                      :x="lane.x"
                      :y="lane.y"
                      :width="diagramWidth"
                      :height="lane.height"
                      fill="transparent"
                      class="lane-rect lane-cell-hit"
                      @contextmenu.prevent="showLaneContextMenu($event, lane, index)"
                    />
                    <rect
                      :x="lane.x"
                      :y="lane.y"
                      :width="diagramWidth"
                      :height="lane.height"
                      class="lane-cell"
                      :data-test="`lane-rect-${lane.name}`"
                      pointer-events="none"
                    />
                    <foreignObject
                      :x="lane.x + LANE_CELL_MENU_OFFSET_X + 24"
                      :y="lane.y + LANE_CELL_MENU_OFFSET_Y"
                      :width="LANE_CELL_MENU_SIZE"
                      :height="LANE_CELL_MENU_SIZE"
                      class="lane-cell-menu-container"
                    >
                      <div v-if="!isEmbedMode" class="lane-cell-menu-wrapper">
                        <button
                          type="button"
                          :id="`lane-menu-button-${lane.name.split(' ').join('-')}`"
                          :data-test="`lane-menu-button-${lane.name.split(' ').join('-')}`"
                          class="lane-cell-menu-button"
                          :disabled="$store.state.isWorkflowDisabled"
                          @click.stop="showLaneCellMenu($event, lane, index)"
                        >
                          <v-icon size="16">mdi-dots-horizontal</v-icon>
                        </button>
                      </div>
                    </foreignObject>
                  </g>
                </template>
              </g>

              <g v-for="group of sortedGroups.slice(1)" :key="group.id">
                <path
                  :d="`M ${group.x} 0 V 999999`"
                  style="stroke: var(--diagram-lane-divider); stroke-width: 1px;"
                ></path>
                <path
                  v-show="dragging && group.id === selectedElement?.id"
                  :d="`M ${groupDragX} 0 V 999999`"
                  style="stroke: var(--diagram-lane-divider); stroke-width: 2px;"
                ></path>
                <rect
                  :data-test="`group-divider-${group.name}`"
                  :id="group.id"
                  :x="group.x - 2" y="0"
                  width="3" height="999999"
                  :class="group.startSlotX === 0 ? 'notDraggable' : 'draggable horizontal'"
                  :fill="dragging && group.id === selectedElement?.id ? 'rgb(46, 156, 230)' : 'transparent'"
                  stroke="transparent"
                >
                </rect>
              </g>

              <!-- Add-group placement preview: grey boundary line that tracks the cursor; dropped on click/release. -->
              <g v-show="placingGroup && placeGroupSlotX !== null" style="pointer-events: none;">
                <path
                  :d="`M ${placeGroupLineX} 0 V 999999`"
                  style="stroke: #9e9e9e; stroke-width: 2px; stroke-dasharray: 6 4;"
                ></path>
                <text :x="placeGroupLineX + 6" y="16" style="fill: #9e9e9e; font-size: 12px;">New group</text>
              </g>

              <template v-for="node in svgGraph">
                <!-- Pre Loop to print parts of the svg that has to be on the lower layer -->
                <!-- Event Path -->
                <template v-for="(parentId, indexParent) in node.parents" :key="node.id + indexParent + '80'">
                  <g v-if="parentId === 'start'">
                    <rect
                      :x="node.x - 26"
                      :y="node.y + 44"
                      width="8"
                      height="8"
                      :style="{ fill: 'var(--diagram-stroke)' }"
                      rx="2"
                    />
                    <line
                      :x1="node.x - 26"
                      :y1="node.y + 48"
                      :x2="node.x + (node.type === 'bpmn:ExclusiveGateway' ? 4.5 : 0)"
                      :y2="node.y + 48"
                      :style="{ stroke: 'var(--diagram-stroke)' }"
                      stroke-width="1"
                    />
                  </g>
                  <circle
                    v-if="svgGraph[parentId]"
                    :cx="svgGraph[parentId].x + (svgGraph[parentId].type === 'bpmn:ExclusiveGateway' ? 102 : 105)"
                    :cy="svgGraph[parentId].y + 96/2"
                    r="1" :style="{ fill: 'var(--diagram-stroke)' }"
                  />
                  <template v-if="svgGraph[parentId]">
                    <template
                      v-for="pathData in [createPathBetweenNodes(parentId, node.id)]"
                      :key="pathData"
                    >
                      <path
                        :d="pathData"
                        class="connection-hit-area"
                        fill="none"
                        pointer-events="stroke"
                        :data-test="`connection-hit-${parentId}-${node.id}`"
                        :data-test-parent="parentId"
                        @mousedown.stop="startConnectionEdit(parentId, node.id, $event)"
                      >
                        <title>Drag to move or remove this connection</title>
                      </path>
                      <path
                        :d="pathData"
                        class="sequence-arrow"
                        :class="{
                          highlighted: isArrowHighlighted(parentId, node.id),
                          dimmed: isConnectionDimmed(parentId, node.id, focusedEventIds, dragging),
                          outlined:
                            node.outlined || svgGraph[node.parents[0]]?.outlined,
                          'editing-hidden': isConnectionBeingEdited(parentId, node.id),
                          backward: isBackwardConnection(parentId, node.id, svgGraph),
                        }"
                      ></path>
                    </template>
                  </template>
                  <!-- labels forward connections -->
                  <template v-if="svgGraph[parentId] && connectionHasLabel(parentId, node.id)">
                    <!-- editable Event Label -->
                    <foreignObject
                      :key="node.id + indexParent + 'event-label'"
                      :x="svgGraph[parentId].x + 140"
                      :y="node.y + 10"
                      width="160"
                      height="32"
                    >
                      <bpmn-event-label
                        v-if="!node.outlined"
                        :node="node"
                        :conditionLabel="getConnectionLabel(node, parentId)"
                        :auto-focus-on-select="true"
                        @label-blur="
                          blurOnConditionLabels($event, node, parentId)
                        "
                        @label-focus="focusOnConditionLabel"
                      ></bpmn-event-label>
                    </foreignObject>
                  </template>
                  <template v-if="svgGraph[parentId] && backwardConnectionHasLabel(parentId, node.id)">
                    <foreignObject
                      :key="node.id + indexParent + 'backward-event-label'"
                      :x="getBackwardLabelPosition(parentId, node.id).x"
                      :y="getBackwardLabelPosition(parentId, node.id).y"
                      width="160"
                      height="32"
                      style="pointer-events: none;"
                    >
                      <bpmn-event-label
                        v-if="!node.outlined"
                        :node="node"
                        :conditionLabel="getConnectionLabel(node, parentId)"
                        :auto-focus-on-select="true"
                        :on-line="true"
                        @label-mousedown="onBackwardLabelFocus"
                        @label-blur="
                          onBackwardLabelBlur($event, node, parentId)
                        "
                        @label-focus="onBackwardLabelFocus"
                      ></bpmn-event-label>
                    </foreignObject>
                  </template>
                </template>
              </template>
              <template v-for="node in svgGraph" :key="node.id">
                <!-- Event -->
                <!-- Event Box -->
                <template v-if="node.type == 'bpmn:Task'">
                  <bpmn-event
                    :key="node.id + '40'"
                    :node="node"
                    :svgGraph="svgGraph"
                    :svgGroups="svgGroups"
                    :svgLanes="svgLanes"
                    :conditionLabel="(svgGroups || [])[node.groupId]?.name || 'null'"
                    :eventDefinition="workflow.eventDefinition"
                    :workflow="workflow"
                    :selected-event-ids="selectedEventIds"
                    :connection-target="
                      enableConnectionTargets &&
                        connectionTargetsIds.includes(node.id)
                    "
                    class="bpmn-event-container"
                    @move-right="$emit('move-right')"
                    @move-left="$emit('move-left')"
                  ></bpmn-event>
                </template>

                <template v-else>
                  <bpmn-gateway
                    :key="node.id + '60'"
                    :node="node"
                    :svgGraph="svgGraph"
                    :svgLanes="svgLanes"
                    :svgGroups="svgGroups"
                    :workflow="workflow"
                    :selected-event-ids="selectedEventIds"
                    :connection-target="
                      enableConnectionTargets &&
                        connectionTargetsIds.includes(node.id)
                    "
                    :eventDefinition="workflow.eventDefinition"
                    @move-right="$emit('move-right')"
                    @move-left="$emit('move-left')"
                  ></bpmn-gateway>
                </template>

                <!-- button menu on selected event or exclusive gateway -->
                <!--
                as for vue v2.6.11:
                Slots do not render in foreignObjects if the foreignObject
                is not in the template of the same component that has
                the root svg element of the svg. Means you can't split svg
                into components properly.

                https://github.com/vuejs/vue/issues/11315
                https://github.com/vuejs/vue/pull/11349

              must be fixed in vue v2.6.13
              -->

                <!-- Add event button -->
                <foreignObject
                  v-if="!multiSelectionActive && !isEmbedMode"
                  :x="node.x + (node.type === 'bpmn:ExclusiveGateway' ? 105 : 108)"
                  :y="node.y + 38"
                  width="40"
                  height="20"
                  class="add-event-button"
                  style="pointer-events: none;"
                  :class="{
                    'show-on-hover': $route.params.eventId !== node.id,
                  }"
                >
                  <v-btn
                    :disabled="$store.state.isWorkflowDisabled"
                    variant="flat"
                    size="16px"
                    class="captures-description"
                    style="pointer-events: auto;"
                    title="Click to add event • Drag to existing event to connect"
                    :class="{
                      'opacity-100' : $route.params.eventId === node.id,
                    }"
                    @mousedown="startConnectionDragFromPlus(node, $event)"
                    @click="handleClickQuickAddEvent($event, node)"
                  >
                    <v-icon style="font-size: 16px;">mds:add</v-icon>
                  </v-btn>
                </foreignObject>

                <!-- colour badges on events and exclusive gateways -->
                <rect
                  v-if="node.type === 'bpmn:Task' || badgeDataByEvent[node.id]?.badges.length > 0"
                  :id="'rb-' + node.id"
                  :x="node.type === 'bpmn:Task' ? node.x : node.x + 28"
                  :y="node.y + 81"
                  :mask="`url(#rmask-${node.id})`"
                  fill="white"
                  opacity="0.3"
                  :width="node.type === 'bpmn:Task' ? 104 : 50"
                  height="15"
                />
                <foreignObject
                  :key="node.id + 'badge'"
                  v-if="cardTypes?.length > 0"
                  :x="node.x"
                  :y="node.y + 81"
                  :mask="`url(#rmask-${node.id})`"
                  width="104"
                  height="16"
                >
                  <div :class="`d-flex ${node.type === 'bpmn:ExclusiveGateway' ? 'justify-center' : 'justify-left'} align-left`">
                    <div
                      v-for="(badge, index) in (badgeDataByEvent[node.id]?.badges.slice(0, badgeDataByEvent[node.id]?.badges.length > 6 ? 5 : 6) || [])"
                      @click="inspectEvent(node.id)"
                      :key="badge.id"
                      :class="['card-badge', { 'd-none': badge.count <= 0, 'ml-05': index !== 0, 'flex-grow-1': badgeDataByEvent[node.id]?.badges.length === 6 }, getCardBgClass(badge)]"
                      :style="getCardBgClass(badge) ? undefined : { backgroundColor: getBackgroundColour(badge) }"
                    >
                      <span
                        class="card-badge-number"
                        :style="'color: ' + getTextColour(badge)"
                      >
                        {{ badge.count > 99 ? `${badge.count.toString()[0]}..` : badge.count }}
                      </span>
                    </div>

                    <div
                      v-if="$route.params.eventId === node.id && badgeDataByEvent[node.id]?.badges.length === 0 && node.type === 'bpmn:Task'"
                      style="background-color: var(--light-1);" class="card-badge"
                      @click="inspectEvent(node.id)"
                    >
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <span v-bind="props" class="card-badge-number">+</span>
                        </template>
                        <span>Add cards using the sidebar</span>
                      </v-tooltip>
                    </div>

                    <div
                      v-if="!!badgeDataByEvent[node.id] && badgeDataByEvent[node.id].badges.length > 6"
                      class="card-badge card-more-badge"
                      @click="inspectEvent(node.id)"
                    >
                      <span class="card-badge-number">
                        +{{
                          badgeDataByEvent[node.id].restCardsCount > 99 ?
                            `${badgeDataByEvent[node.id].restCardsCount.toString()[0]}..` :
                            badgeDataByEvent[node.id].restCardsCount
                        }}
                      </span>
                    </div>
                  </div>
                </foreignObject>
              </template>

              <!-- Connection ghost path while dragging from + button -->
              <path
                v-if="connectionDrag && connectionDrag.started && ghostConnectionPath"
                class="connection-ghost-line"
                :class="{ 'over-target': connectionDragOverTarget }"
                :d="ghostConnectionPath"
              />

              <!-- Connection ghost path while editing an existing connection -->
              <path
                v-if="connectionEdit && connectionEdit.started && ghostEditConnectionPath"
                class="connection-ghost-line"
                :class="{ 'over-target': connectionEditOverTarget }"
                :d="ghostEditConnectionPath"
              />

              <!-- definitions of arrow -->
              <defs>
                <marker
                  id="sequenceflow-end-white-black-1r79xjtcycpwpzz6dais2xasb"
                  viewBox="0 0 40 40"
                  refX="17"
                  refY="23"
                  markerWidth="40"
                  markerHeight="40"
                  orient="0"
                >
                  <path
                    d="M 22.208 22.226 C 22.807 22.622 22.807 23.5 22.208 23.896 L 19.773 25.502 C 19.108 25.941 18.223 25.464 18.223 24.667 L 18.223 21.455 C 18.223 20.658 19.108 20.181 19.773 20.62 L 22.208 22.226 Z"
                    style="stroke-width: 0 !important; fill: var(--diagram-stroke)"
                  />
                </marker>
                <marker
                  id="connection-highlighted-arrow-end"
                  viewBox="0 0 40 40"
                  refX="17"
                  refY="23"
                  markerWidth="20"
                  markerHeight="20"
                  orient="0"
                >
                  <path
                    d="M 22.208 22.226 C 22.807 22.622 22.807 23.5 22.208 23.896 L 19.773 25.502 C 19.108 25.941 18.223 25.464 18.223 24.667 L 18.223 21.455 C 18.223 20.658 19.108 20.181 19.773 20.62 L 22.208 22.226 Z"
                    style="stroke-width: 0 !important; fill: var(--diagram-stroke)"
                  />
                </marker>
                <marker
                  id="connection-ghost-arrow-end"
                  viewBox="0 0 40 40"
                  refX="17"
                  refY="23"
                  markerWidth="20"
                  markerHeight="20"
                  orient="0"
                >
                  <path
                    d="M 22.208 22.226 C 22.807 22.622 22.807 23.5 22.208 23.896 L 19.773 25.502 C 19.108 25.941 18.223 25.464 18.223 24.667 L 18.223 21.455 C 18.223 20.658 19.108 20.181 19.773 20.62 L 22.208 22.226 Z"
                    style="stroke-width: 0 !important; fill: rgb(46, 156, 230)"
                  />
                </marker>
                <marker
                  id="connection-hover-arrow-end"
                  viewBox="0 0 40 40"
                  refX="17"
                  refY="23"
                  markerWidth="20"
                  markerHeight="20"
                  orient="0"
                >
                  <path
                    d="M 22.208 22.226 C 22.807 22.622 22.807 23.5 22.208 23.896 L 19.773 25.502 C 19.108 25.941 18.223 25.464 18.223 24.667 L 18.223 21.455 C 18.223 20.658 19.108 20.181 19.773 20.62 L 22.208 22.226 Z"
                    style="stroke-width: 0 !important; fill: rgb(46, 156, 230)"
                  />
                </marker>
              </defs>

              <!-- Lane drag-reorder overlay: insertion line (where it will switch to) + a 50%
                   alpha ghost of the dragged lane following the cursor. Drawn last so it sits on
                   top of everything. -->
              <g v-if="laneReorder.active && svgLanes[laneReorder.laneId]" style="pointer-events: none;">
                <line
                  :x1="0"
                  :y1="svgLanes[laneReorder.laneId].y"
                  :x2="diagramWidth"
                  :y2="svgLanes[laneReorder.laneId].y"
                  stroke="rgb(46, 156, 230)"
                  stroke-width="3"
                  stroke-dasharray="6 4"
                />
                <rect
                  :x="0"
                  :y="laneReorder.ghostTop"
                  :width="diagramWidth"
                  :height="laneReorder.draggedHeight"
                  :fill="laneGhostFill"
                  stroke="rgb(46, 156, 230)"
                  stroke-width="2"
                  stroke-dasharray="6 4"
                  rx="2"
                />
                <text
                  :x="36"
                  :y="laneReorder.ghostTop + 20"
                  fill="var(--diagram-stroke)"
                  font-size="14"
                  font-weight="600"
                >{{ laneReorder.draggedName }}</text>
              </g>
            </svg>
          </svg>

          <div
            v-if="selectionRect"
            class="selection-box"
            data-test="selection-box"
            :style="selectionBoxStyle"
          ></div>
          <div
            v-else-if="selectionBoundingRect"
            class="selection-bounds"
            data-test="selection-bounds"
            :style="selectionBoundingRectStyle"
            @mousedown="handleSelectionBoundsMousedown"
          ></div>

          <!-- Error message tooltip -->
          <div
            v-if="invalidSelectionMessage && (selectionRect || selectionBoundingRect)"
            class="selection-error-message"
            data-test="selection-error-message"
            :style="selectionErrorMessageStyle"
          >
            {{ invalidSelectionMessage }}
          </div>

          <transition name="actions">
            <div v-if="showActions">
              <div
                v-if="
                  primarySelectedEvent &&
                    !$store.state.isWorkflowDisabled
                "
                :style="`
                position: absolute;
                left: ${getActionsMenuXPosition()}px;
                top: ${constrainActionsMenuYPosition()}px;
                transition: left ease 0.15s, top ease 0.15s, transform ease 0.15s;
                height: 21;
                ${selectionBoundingRect ? 'transform: translateX(-50%);' : ''}
              `"
                class="text-left"
              >
                <bpmn-diagram-actions
                  :selectedEvent="primarySelectedEvent"
                  :selectedEventIds="selectedEventIds"
                  :enableConnectionTargets="enableConnectionTargets"
                  :events="events"
                  :baseData="baseData"
                  :svgGraph="svgGraph"
                  :svgGroups="svgGroups"
                  :svgLanes="svgLanes"
                  :workflow="workflow"
                  :disable-single-selection-actions="multiSelectionActive"
                  :color="
                    primarySelectedEvent?.color
                      ? primarySelectedEvent.color.rgb
                      : workflow.eventDefinition.color.rgb
                  "
                  @on-copy="$emit('on-copy')"
                  @on-paste="$emit('on-paste')"
                  @on-cut="$emit('on-cut')"
                  @change-shape="showTransformOptions = !showTransformOptions"
                  @add-shape="addShape"
                  @add-shape-ai="addShapeAI"
                  @add-parallel-event="addParallelEvent"
                  @add-group="startPlaceGroup"
                  @clear-multi-selection="clearMultiSelection"
                />
              </div>
            </div>
          </transition>

          <!-- Shape Right is moved into BpmnDiagramActions above
          <div
            v-if="
              $route.params.eventId &&
              svgGraph?.[$route.params.eventId] &&
              !$store.state.isWorkflowDisabled
            "
            v-show="!dragging"
            :style="`
            position: absolute;
            left: ${svgGraph?.[$route.params.eventId]?.x + 105}px;
            top: ${svgGraph?.[$route.params.eventId]?.y - 1 + diagramYOffset}px;
            width: 22;
            height: 105;
          `"
          >
            <bpmn-diagram-actions-shape-right
              v-if="svgGraph[$route.params.eventId]"
              :selectedEvent="svgGraph[$route.params.eventId]"
              :baseData="baseData"
              @add-shape="addShape"
              :svgGraph="svgGraph"
              :svgGroups="svgGroups"
              :svgLanes="svgLanes"
            ></bpmn-diagram-actions-shape-right>
          </div>
          -->

          <!-- Subprocess icon -->
          <div
            v-for="node in subprocesses"
            :key="node.id + '-subprocess'"
            width="12"
            height="12"
            style="line-height: 1em; font-size: 0"
            class="subprocess-icon"
            :style="`
            position: absolute;
            left: ${svgGraph?.[node.id]?.x + 90}px;
            top: ${svgGraph?.[node.id]?.y + 2 + diagramYOffset}px;
            width: 12px;
            height: 12px;
          `"
          >
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ on, props }">
                <router-link
                  :to="
                    '/workflow/' +
                      $route.params.projectId +
                      '/' +
                      node.subprocess.id
                  "
                  target="_blank"
                >
                  <v-icon v-bind="props" size="12" v-on="{ ...on }">custom:SubprocessIcon</v-icon>
                </router-link>
              </template>
              <span>Open subprocess: {{ node.subprocess.name }}</span>
            </v-tooltip>
          </div>
        </div>

        <div
          class="context-menu-top"
          v-if="svgGraph?.[$route.params.eventId]?.x && showTransformOptions"
          :style="`
            position: absolute;
            left: ${
            svgGraph?.[$route.params.eventId]?.x +
            56 -
            bpmnScrollLeft
          }px;
            top: ${svgGraph?.[$route.params.eventId]?.y - 48 - scrollTop + diagramYOffset}px;
            width: 70;
            height: 21;
          `"
          v-show="!dragging"
        >
          <bpmn-diagram-actions-shape-top-transform
            class="dark position-fixed"
            :selectedEvent="svgGraph[$route.params.eventId]"
            :baseData="baseData"
            :svgGraph="svgGraph"
            :svgGroups="svgGroups"
            :svgLanes="svgLanes"
            @transform-shape="hideTransformOptions"
            v-click-outside="closeTransformOptions()"
          />
        </div>
        <!-- Swimlane titles. Wrapped so embed mode can scale this HTML overlay by the
             same factor as the viewBox-scaled SVG (it lives outside the SVG). -->
        <div :style="laneTitlesOverlayStyle">
          <div
            v-for="lane in svgLanes"
            :key="lane.id"
            :style="`
              width: 30px;
              position: absolute;
              left: ${lane.x}px;
              top: ${lane.y + diagramYOffset}px;
              height: ${lane.height}px;
            `"
          >
            <!-- editable Lane name (rotated) -->
            <div class="lane-title-container position-relative" style="z-index: 12;">
              <bpmn-swim-lane-title
                :first="lane.index === 0 && !hasGroups"
                :last="lane.index === noOfLanes - 1"
                :lane="lane"
                :index="lane.index"
                :svgGraph="svgGraph"
                :svgLanes="svgLanes"
                :svgGroups="svgGroups"
                :workflow="workflow"
                @name-blur="blurOnRoleName"
                @reorder-start="startLaneReorder"
              />

            </div>
          </div>
        </div>
      </div>

      <!-- Context menu for lanes -->
      <v-menu
        ref="laneContextMenuRef"
        v-model="laneContextMenu.show"
        :location="laneContextMenu.location"
        :close-on-content-click="true"
        :transition="false"
      >
        <template #activator="{ props }">
          <div
            ref="laneContextMenuActivator"
            v-bind="props"
            style="position: fixed; width: 1px; height: 1px; z-index: -1;"
            :style="{
              left: (laneContextMenu.activator?.x || 0) + 'px',
              top: (laneContextMenu.activator?.y || 0) + 'px'
            }"
          ></div>
        </template>
        <v-list density="compact">
          <v-tooltip location="right">
            <template #activator="{ props: itemProps }">
              <div v-bind="itemProps">
                <v-list-item
                  :disabled="!clipboardHasValidJson"
                  prepend-icon="mdi-content-paste"
                  @click="clipboardHasValidJson && handleContextMenuAction('paste')"
                  data-test="paste-button"
                >
                  <v-list-item-title>Paste</v-list-item-title>
                </v-list-item>
              </div>
            </template>
            <span v-if="clipboardHasValidJson">
              Paste event(s) from clipboard
            </span>
            <span v-else>No valid event data in clipboard</span>
          </v-tooltip>

          <v-divider />

          <v-list-item
            @click="handleContextMenuAction('add-start-point')"
            data-test="add-start-point-button"
            prepend-icon="mds:add-circle"
          >
            <v-list-item-title>Add start point here</v-list-item-title>
          </v-list-item>

          <v-divider />

          <v-list-item
            @click="handleContextMenuAction('add-lane-above')"
            data-test="add-lane-above-button"
            prepend-icon="mdi-arrow-up"
          >
            <v-list-item-title>Add lane above</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="handleContextMenuAction('add-lane-below')"
            data-test="add-lane-below-button"
            prepend-icon="mdi-arrow-down"
          >
            <v-list-item-title>Add lane below</v-list-item-title>
          </v-list-item>
          <v-tooltip v-if="!isLaneEmpty(laneContextMenu.laneId)" location="right">
            <template #activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-list-item :disabled="true" data-test="delete-role-button" prepend-icon="mds:delete">
                  <v-list-item-title>Delete lane</v-list-item-title>
                </v-list-item>
              </div>
            </template>
            <span>Cannot delete a lane that is not empty.</span>
          </v-tooltip>
          <v-list-item
            v-else
            @click="handleContextMenuAction('delete-lane')"
            data-test="delete-role-button"
            prepend-icon="mds:delete"
          >
            <v-list-item-title>Delete lane</v-list-item-title>
          </v-list-item>

          <v-divider />

          <v-list-item
            v-if="sortedGroups.length === 0"
            @click="handleContextMenuAction('add-group-before')"
            prepend-icon="mdi-plus"
          >
            <v-list-item-title>Add group</v-list-item-title>
          </v-list-item>
          <template v-else>
            <v-list-item
              @click="handleContextMenuAction('add-group-before')"
              prepend-icon="mdi-arrow-left"
            >
              <v-list-item-title>Add group before</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="handleContextMenuAction('add-group-after')"
              prepend-icon="mdi-arrow-right"
            >
              <v-list-item-title>Add group after</v-list-item-title>
            </v-list-item>
          </template>
          <v-tooltip
            v-if="!laneContextMenu.group"
            location="right"
          >
            <template #activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-list-item :disabled="true" data-test="ungroup-button" prepend-icon="mdi-ungroup">
                  <v-list-item-title>Ungroup</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="true" data-test="delete-group-button" prepend-icon="mds:delete">
                  <v-list-item-title>Delete group</v-list-item-title>
                </v-list-item>
              </div>
            </template>
            <span>No group to ungroup or delete.</span>
          </v-tooltip>
          <template v-else>
            <v-list-item
              data-test="ungroup-button"
              @click="handleContextMenuAction('ungroup')"
              prepend-icon="mdi-ungroup"
            >
              <v-list-item-title>Ungroup</v-list-item-title>
            </v-list-item>
            <v-list-item
              base-color="error"
              data-test="delete-group-button"
              @click="handleContextMenuAction('delete-group')"
              prepend-icon="mds:delete"
            >
              <v-list-item-title>Delete group</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>

      <bpmn-diagram-actions-bottom
        v-if="!isEmbedMode"
        :selectedEvent="svgGraphData[$route.params.eventId]"
        :cardTypes="cardTypes"
        :base-data="baseData"
        :svgGraph="svgGraph"
        :svgGroups="svgGroups"
        :svgLanes="svgLanes"
        @add-shape="addShape"
        @add-group="startPlaceGroup"
        @add-lane="addRole"
      />

      <!-- Read-only embed: view controls in place of the editing toolbar -->
      <div v-if="isEmbedMode" class="embed-zoom-controls" :class="{ 'embed-controls-visible': embedControlsVisible }">
        <v-tooltip location="left" text="Zoom in">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon="mdi-plus" size="x-small" variant="text" :disabled="!embedCanZoomIn" @click="embedZoomIn" />
          </template>
        </v-tooltip>
        <v-tooltip location="left" text="Zoom out">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon="mdi-minus" size="x-small" variant="text" :disabled="!embedCanZoomOut" @click="embedZoomOut" />
          </template>
        </v-tooltip>
        <v-tooltip location="left" text="Fit to screen">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon="mdi-fit-to-page-outline" size="x-small" variant="text" @click="embedZoomReset" />
          </template>
        </v-tooltip>
      </div>

      <a
        v-if="isEmbedMode"
        class="embed-attribution"
        :class="{ 'embed-controls-visible': embedControlsVisible }"
        :href="embedFullUrl"
        target="_blank"
        rel="noopener"
      >
        <img class="embed-attribution-logo" :src="brand.assets.favicon" alt="" />
        <span>Open in {{ brand.name }}</span>
      </a>

      <!-- Frozen group-header row (embed only): pinned to the top during vertical scroll,
           tracks the columns horizontally. -->
      <div
        v-if="isEmbedMode && hasGroups"
        class="embed-group-headers"
        :style="{ height: (GROUP_HEIGHT * embedZoom) + 'px' }"
      >
        <div
          v-for="group in svgGroups"
          :key="'embed-gh-' + group.id"
          class="embed-group-header"
          :style="embedGroupHeaderStyle(group)"
        >{{ group.name }}</div>
      </div>
    </div>

    <bpmn-generator
      :bpm="thisComponent()"
      :workflow="workflow"
      :workflowTasks="events ? events : {}"
      ref="generator"
    />

    <start-with-mcp-dialog v-model="showMcpDialog" />
  </div>
</template>

<script>
import BpmnEvent from '@/components/BpmnEvent'
import BpmnGateway from '@/components/BpmnGateway'
import BpmnDiagramActions from '@/components/BpmnDiagramActions'
import BpmnDiagramActionsBottom from '@/components/BpmnDiagramActionsBottom'
import { isEmbedMode as isEmbedRoute } from '@/utils/embed'
import BpmnSwimLaneTitle from '@/components/BpmnSwimLaneTitle'
import BpmnSidePanel from '@/components/BpmnSidePanel'
import BpmnDiagramActionsShapeTopTransform from '@/components/BpmnDiagramActionsShapeTopTransform'
import BpmnEventLabel from '@/components/BpmnEventLabel'
// import BpmnEventLabelVertical from '@/components/BpmnEventLabelVertical'
import workflowApi from '@/api/workflowApi'
import { workflowApiHelpers } from '@/api/workflowApiHelpers'
import cardTypesApi from '@/api/cardTypesApi'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import { EventBus } from '@/event-bus/event-bus.js'
import BpmnGenerator from '@/components/BpmnGenerator'
import StartWithMcpDialog from '@/components/StartWithMcpDialog.vue'
import { mapGetters, mapMutations, mapState } from 'vuex'
import randomUUID from '@/utils/uuid'
import { getColorName } from '@/utils/color'
import { scrollToEvent } from '@/utils/scroll'
import safeStructuredClone from '@/utils/clone'
import { recomputeLaneLayout } from '@/utils/diagramReorder'
import dragFunctions from '@/helpers/dragFunctions'
import positionHelpers from '@/helpers/positionHelpers'
import { computeDropIndex, computeGhost, groupWidthForDrop } from '@/helpers/groupReorderGeometry'
import dataHelpers from '@/helpers/dataHelpers'
import svgPathHelpers from '@/helpers/svgPathHelpers'
import { buildConnectionChannelOffsets, connectionChannelKey } from '@/helpers/connectionChannels'
import { updateFontSizeDynamically } from '@/utils/update-font'
import copyPasteHelpers from '@/helpers/copyPasteHelpers'
import cardHelpers from '@/helpers/cardHelpers'
import brand from '@/composables/useBrand'

// Zoom bounds for the read-only embed view (no zoom exists in the editor).
const EMBED_ZOOM_MIN = 0.2
const EMBED_ZOOM_MAX = 2
const EMBED_ZOOM_STEP = 0.1

// Width of the group header ⋯ button, per .group-actions-button.
const GROUP_ACTIONS_BUTTON_WIDTH = 20

export default {
  name: 'Bpmn',
  components: {
    BpmnEvent,
    BpmnEventLabel,
    BpmnGateway,
    BpmnDiagramActions,
    BpmnDiagramActionsBottom,
    BpmnSwimLaneTitle,
    BpmnSidePanel,
    BpmnDiagramActionsShapeTopTransform,
    BpmnGenerator,
    StartWithMcpDialog
  },
  data () {
    return {
      embedZoom: 1,
      embedUserZoomed: false,
      embedControlsVisible: false,
      aiGenerator: false,
      showMcpDialog: false,
      selectedElement: null,
      timeout: null,
      dragOffsetX: null,
      dragOffsetY: null,
      startDragX: null,
      startDragY: null,
      slotX: 0,
      groupDragSnapshot: null,
      placingGroup: false,
      placeGroupSlotX: null,
      placeGroupPointerDown: false,
      watchInPause: false,
      svgGraph: null,
      svgLanes: null,
      cardsByEventId: {},
      svgGroups: {},
      // savingChanges: false,
      dragging: false,
      laneReorder: { active: false, laneId: null, dropIndex: -1 },
      editingGroupId: null,
      groupDragCandidate: null,
      groupReorder: {
        active: false,
        groupId: null,
        fromIndex: -1,
        dropIndex: -1,
        ghostLeft: 0,
        ghostWidth: 0,
        draggedWidth: 0,
        firstGroupExtra: 0,
        draggedName: ''
      },
      showActions: false,
      editingBackwardLabel: false,
      latestEventId: null,
      enableConnectionTargets: false,
      showTransformOptions: false,
      connectionDrag: null,
      connectionDragJustFinished: false,
      connectionDragJustFinishedTimeout: null,
      connectionEdit: null,
      triggerUpdateFromDrag: false,
      autoscroll: null,
      clientY: null,
      scrollTop: null,
      scrollLeft: null,
      occupiedSlotsVerticalLines: {},
      creatingEvent: false,
      draggingCanvasStartX: 0,
      draggingCanvasStartY: 0,
      embedPanMoved: false,
      isDraggingCanvas: false,
      isSelecting: false,
      selectionStart: null,
      selectionRect: null,
      selectionEvents: [],
      selectedEventIds: [],
      // Movement only: 'full' = horizontal reorder + lane change, 'verticalOnly' = lane
      // change only. Delete/cut/copy are gated separately via canMultiSelectionBeModified.
      selectionCapability: 'full',
      selectionBoundingRect: null,
      invalidSelectionMessage: null,
      isSelectionDragging: false,
      selectionDragStart: null,
      selectionDragInitialRect: null,
      selectionDragInitialNodes: [],
      multiSelectionSlotX: 0,
      multiSelectionInputSlotX: 0,
      multiSelectionOutputSlotX: 0,
      multiSelectionInputAnchorId: null,
      multiSelectionOutputAnchorId: null,
      justFinishedDragging: false,
      justFinishedSelecting: false,
      laneContextMenu: {
        show: false,
        location: 'bottom',
        activator: null,
        laneId: null,
        laneIndex: null,
        group: null
      },
      clipboardHasValidJson: false,
      visibleBoardRight: 0,
      LANE_WIDTH: 24,
      GROUP_HEIGHT: 24,
      DEFAULT_RIGHT_OFFSET: 2,
      LANE_CELL_MENU_OFFSET_X: 6,
      LANE_CELL_MENU_OFFSET_Y: 6,
      LANE_CELL_MENU_SIZE: 24
    }
  },
  props: [
    'showDone'
  ],
  mixins: [diagramHelpers],
  computed: {
    brand: () => brand,
    ...mapGetters({
      baseData: 'svgGraphData/baseData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGraphData: 'svgGraphData/svgGraphData',
      svgGroupsData: 'svgGraphData/svgGroupsData',
      diagramWidth: 'synchronizedScrolling/diagramWidth',
      loadingWorkflowUpdate: 'workflow/getLoadingUpdate'
    }),
    connectionChannelOffsets () {
      return buildConnectionChannelOffsets(this.svgGraph)
    },
    // The opened event plus anything caught in a multi-selection. Connections touching one of these
    // stay at full strength; the rest fade.
    focusedEventIds () {
      const ids = new Set(this.selectedEventIds || [])
      if (this.$route.params.eventId) {
        ids.add(this.$route.params.eventId)
      }

      return ids
    },
    ghostConnectionPath () {
      if (!this.connectionDrag || !this.connectionDrag.started) {
        return ''
      }

      return this.buildGhostPathFromTo(
        this.connectionDrag.sourceId,
        this.connectionDrag.currentX,
        this.connectionDrag.currentY,
        this.connectionDrag.hoveredTargetId
      )
    },
    ghostEditConnectionPath () {
      if (!this.connectionEdit || !this.connectionEdit.started) {
        return ''
      }

      return this.buildGhostPathFromTo(
        this.connectionEdit.parentId,
        this.connectionEdit.currentX,
        this.connectionEdit.currentY,
        this.connectionEdit.hoveredTargetId
      )
    },
    connectionDragOverTarget () {
      return !!this.connectionDrag?.hoveredTargetId
    },
    connectionEditOverTarget () {
      return !!this.connectionEdit?.hoveredTargetId
    },
    ...mapState({
      workflow: state => state.workflow.workflow,
      cardTypes: state => state.cardTypes.cardTypes,
      events: (state) => state.workflow.workflow.domainEvents || [],
      showReleaseNo: state => state.workflow.showReleaseNo,
      bpmnScrollLeft: state => state.synchronizedScrolling.bpmnScrollLeft,
      sidePanelTab: state => state.sidePanelTab,
      showSidePanel: state => state.showSidePanel,
      hasClosedSidePanel: state => state.hasClosedSidePanel,
      svgContainerWidth: state => state.synchronizedScrolling.svgContainerWidth
    }),
    hasGroups () {
      return Object.keys(this.svgGroups)?.length > 0
    },
    diagramYOffset () {
      return this.hasGroups ? 24 : 0
    },
    // Read-only embed: hides all editing affordances and enables the zoom overlay.
    isEmbedMode () {
      return isEmbedRoute(this.$route)
    },
    embedCanZoomIn () {
      return this.embedZoom < EMBED_ZOOM_MAX
    },
    embedCanZoomOut () {
      return this.embedZoom > EMBED_ZOOM_MIN
    },
    // Full (non-embed) board URL, opened in a new tab from the embed attribution link.
    embedFullUrl () {
      return `${window.location.origin}/workflow/${this.$route.params.projectId}/${this.$route.params.id}`
    },
    // Scales the lane-title HTML overlay to match the viewBox-scaled SVG in embed mode.
    // `display: contents` keeps it layout-neutral in the editor.
    laneTitlesOverlayStyle () {
      if (!this.isEmbedMode) {
        return { display: 'contents' }
      }
      return {
        position: 'absolute',
        top: '0',
        left: '0',
        transform: `scale(${this.embedZoom})`,
        transformOrigin: '0 0'
      }
    },
    flowChartHeight () {
      let res = this.diagramYOffset
      for (const lane in this.svgLanes) {
        res = res + this.svgLanes[lane].height
      }
      if (Object.values(this.svgLanes || {}).length === 0) res = 156
      return res
    },
    noOfLanes () {
      return Object.keys(this.svgLanes).length
    },
    laneGhostFill () {
      const color = this.workflow?.roleDefinition?.color
      if (!color || color.red === undefined) {
        return 'rgba(46, 156, 230, 0.4)'
      }

      return `rgba(${color.red}, ${color.green}, ${color.blue}, 0.5)`
    },
    eventsById () {
      if (!this.events) { return {} }
      return this.events.reduce((accum, curr) => {
        accum[curr.id] = curr
        return accum
      }, {})
    },
    listValidTargetsForSequenceArrows () {
      const { eventId } = this.$route.params

      if (!eventId || !Array.isArray(this.events)) {
        return []
      }

      return this.events.filter((wt) =>
        (wt.id !== eventId) &&
        (!wt.parents || !wt.parents.find(parent => parent === eventId))
      )
    },
    connectionTargetsIds () {
      return this.listValidTargetsForSequenceArrows.map(({ id }) => id)
    },
    subprocesses () {
      return this.events ? this.events.filter(event => event.subprocess) : []
    },
    groupDragX () {
      return positionHelpers.getGroupXPointFromSlot(this.slotX)
    },
    placeGroupLineX () {
      return positionHelpers.getGroupXPointFromSlot(this.placeGroupSlotX || 0)
    },
    slotToGroupMap () {
      const groups = Object.values(this.svgGroups)
      if (groups?.length > 0) {
        return dataHelpers.slotToGroupMap(groups, this.baseData.columns.length)
      } else {
        return {}
      }
    },
    badgeDataByEvent () {
      const badgeByEvent = {}
      Object.values(this.svgGraph).forEach(node => {
        badgeByEvent[node.id] = this.computeEventBadgesData(node)
      })
      return badgeByEvent
    },
    primarySelectedEventId () {
      if (Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 0) {
        return this.selectedEventIds[0]
      }
      return this.$route.params.eventId || null
    },
    primarySelectedEvent () {
      const id = this.primarySelectedEventId
      if (!id) {
        return null
      }
      return this.svgGraph?.[id] || null
    },
    multiSelectionActive () {
      return Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 1
    },
    selectionBoxStyle () {
      if (!this.selectionRect) {
        return {}
      }
      const { x, y, width, height } = this.selectionRect
      const { borderColor, bgColor } = this.selectionPalette
      return {
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
        borderColor,
        backgroundColor: bgColor
      }
    },
    selectionPalette () {
      // full = blue (move any direction), verticalOnly = amber (lane change only).
      switch (this.selectionCapability) {
        case 'verticalOnly':
          return { borderColor: '#e0991a', bgColor: 'rgba(224, 153, 26, 0.15)', cursor: 'ns-resize' }
        default:
          return { borderColor: '#2e9ce6', bgColor: 'rgba(46, 156, 230, 0.15)', cursor: 'move' }
      }
    },
    selectionBoundingRectStyle () {
      if (!this.selectionBoundingRect) {
        return {}
      }
      const { x, y, width, height } = this.selectionBoundingRect
      const capability = this.selectionCapability
      const { borderColor, cursor } = this.selectionPalette
      const borderStyle = capability === 'full' ? 'solid' : 'dashed'
      const bgColor = capability === 'verticalOnly'
        ? 'rgba(224, 153, 26, 0.08)'
        : 'rgba(46, 156, 230, 0.06)'
      return {
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
        border: `1px ${borderStyle} ${borderColor}`,
        backgroundColor: bgColor,
        pointerEvents: 'auto',
        cursor
      }
    },
    selectionErrorMessageStyle () {
      const container = this.$refs.scrollContainer
      if (!container) {
        return { display: 'none' }
      }

      const rect = this.selectionRect || this.selectionBoundingRect
      if (!rect) {
        return { display: 'none' }
      }

      const containerRect = container.getBoundingClientRect()

      // Calculate fixed position relative to viewport
      // rect.x and rect.y are relative to the scroll container's content
      const centerX = containerRect.left + rect.x - container.scrollLeft + (rect.width / 2)
      const topY = containerRect.top + rect.y - container.scrollTop - 28

      return {
        left: `${centerX}px`,
        top: `${topY}px`,
        transform: 'translateX(-50%)',
        // Match the hint colour to the selection tier (blue = movable, amber = lane change only).
        '--hint-color': this.selectionPalette.borderColor
      }
    },
    isGrabbable () {
      return this.svgContainerWidth < this.diagramWidth
    },
    sortedGroups () {
      if (!this.hasGroups) {
        return []
      }

      return Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    }
  },
  methods: {
    updateFontSizeDynamically,
    ...mapMutations({
      setBpmnScrollLeft: 'synchronizedScrolling/setBpmnScrollLeft',
      setSvgContainerWidth: 'synchronizedScrolling/setSvgContainerWidth'
    }),
    handleClickQuickAddEvent (event, node) {
      // If a drag-to-connect just finished, suppress the click-to-add behavior
      if (this.connectionDragJustFinished) {
        return
      }
      // blur the button to avoid focus issues
      document.activeElement.blur()
      if (this.$store.state.isWorkflowDisabled) {
        return
      }
      this.addShape({ parents: [node.id] }, { parentEventId: node.id })
    },
    startConnectionDragFromPlus (node, event) {
      if (event?.button !== 0 || this.$store.state.isWorkflowDisabled) {
        return
      }
      const sourceX = node.x + (node.type === 'bpmn:ExclusiveGateway' ? 105 : 108)
      const sourceY = node.y + 48
      this.connectionDrag = {
        sourceId: node.id,
        sourceX,
        sourceY,
        currentX: sourceX,
        currentY: sourceY,
        startClientX: event.clientX,
        startClientY: event.clientY,
        started: false,
        hoveredTargetId: null
      }
      // Disable text selection during connection drag
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', this.updateConnectionDrag)
      document.addEventListener('mouseup', this.endConnectionDrag, { once: true })
      document.addEventListener('keydown', this.onConnectionKeydown, true)
    },
    updateConnectionDrag (event) {
      if (!this.connectionDrag) {
        return
      }
      const dx = event.clientX - this.connectionDrag.startClientX
      const dy = event.clientY - this.connectionDrag.startClientY
      if (!this.connectionDrag.started && Math.hypot(dx, dy) > 5) {
        this.connectionDrag.started = true
      }
      if (this.connectionDrag.started) {
        const coords = this.clientToSvgCoords(event)
        this.connectionDrag.currentX = coords.x
        this.connectionDrag.currentY = coords.y
        const targetId = this.findEventIdAtPoint(event.clientX, event.clientY)
        this.connectionDrag.hoveredTargetId = (targetId && targetId !== this.connectionDrag.sourceId) ? targetId : null
      }
    },
    endConnectionDrag (event) {
      document.removeEventListener('mousemove', this.updateConnectionDrag)
      document.removeEventListener('keydown', this.onConnectionKeydown, true)
      // Re-enable text selection
      document.body.style.userSelect = ''
      const drag = this.connectionDrag
      this.connectionDrag = null
      if (!drag || !drag.started) {
        return
      }
      this.connectionDragJustFinished = true
      if (this.connectionDragJustFinishedTimeout) {
        clearTimeout(this.connectionDragJustFinishedTimeout)
      }
      this.connectionDragJustFinishedTimeout = setTimeout(() => {
        this.connectionDragJustFinished = false
        this.connectionDragJustFinishedTimeout = null
      }, 100)
      const targetId = this.findEventIdAtPoint(event.clientX, event.clientY)
      if (!targetId || targetId === drag.sourceId || !this.svgGraph[targetId]) {
        return
      }
      const targetNode = this.svgGraph[targetId]
      if (Array.isArray(targetNode.parents) && targetNode.parents.includes(drag.sourceId)) {
        return
      }
      workflowApi.addConnectionToEvent(drag.sourceId, targetId, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    clientToSvgCoords (event) {
      const flowchart = this.$refs.flowchart
      if (!flowchart) {
        return { x: 0, y: 0 }
      }
      const rect = flowchart.getBoundingClientRect()

      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top - this.diagramYOffset
      }
    },
    dropHitBox (node) {
      // Enlarged drop area so a connection can be released just outside a shape,
      // with extra room on the left for the incoming connection point.
      const PAD_LEFT = 32
      const PAD_RIGHT = 16
      const PAD_Y = 32
      const height = node.withoutActions ? 80 : 96

      return {
        x: node.x - PAD_LEFT,
        y: node.y - PAD_Y,
        width: 104 + PAD_LEFT + PAD_RIGHT,
        height: height + PAD_Y * 2
      }
    },
    findEventIdAtPoint (clientX, clientY) {
      const { x, y } = this.clientToSvgCoords({ clientX, clientY })
      // Closest centre wins when enlarged boxes overlap, so the result is stable.
      let bestId = null
      let bestDist = Infinity
      for (const id in this.svgGraph) {
        const node = this.svgGraph[id]
        if (!node || typeof node.x !== 'number' || typeof node.y !== 'number') {
          continue
        }
        const box = this.dropHitBox(node)
        if (x < box.x || x > box.x + box.width || y < box.y || y > box.y + box.height) {
          continue
        }
        const dx = x - (box.x + box.width / 2)
        const dy = y - (box.y + box.height / 2)
        const distSq = dx * dx + dy * dy // squared distance avoids a sqrt
        if (distSq < bestDist) {
          bestDist = distSq
          bestId = id
        }
      }

      return bestId
    },
    isConnectionBeingEdited (parentId, childId) {
      return !!this.connectionEdit?.started &&
        this.connectionEdit.parentId === parentId &&
        this.connectionEdit.originalChildId === childId
    },
    startConnectionEdit (parentId, childId, event) {
      if (event?.button !== 0 || this.$store.state.isWorkflowDisabled) {
        return
      }
      const parentNode = this.svgGraph[parentId]
      if (!parentNode) {
        return
      }
      const sourceX = parentNode.x + (parentNode.type === 'bpmn:ExclusiveGateway' ? 105 : 108)
      const sourceY = parentNode.y + 48
      this.connectionEdit = {
        parentId,
        originalChildId: childId,
        sourceX,
        sourceY,
        currentX: sourceX,
        currentY: sourceY,
        startClientX: event.clientX,
        startClientY: event.clientY,
        started: false,
        hoveredTargetId: null
      }
      // Disable text selection during connection edit drag
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', this.updateConnectionEdit)
      document.addEventListener('mouseup', this.endConnectionEdit, { once: true })
      document.addEventListener('keydown', this.onConnectionKeydown, true)
    },
    updateConnectionEdit (event) {
      if (!this.connectionEdit) {
        return
      }
      const dx = event.clientX - this.connectionEdit.startClientX
      const dy = event.clientY - this.connectionEdit.startClientY
      if (!this.connectionEdit.started && Math.hypot(dx, dy) > 5) {
        this.connectionEdit.started = true
      }
      if (this.connectionEdit.started) {
        const coords = this.clientToSvgCoords(event)
        this.connectionEdit.currentX = coords.x
        this.connectionEdit.currentY = coords.y
        const targetId = this.findEventIdAtPoint(event.clientX, event.clientY)
        this.connectionEdit.hoveredTargetId = (targetId && targetId !== this.connectionEdit.parentId) ? targetId : null
      }
    },
    endConnectionEdit (event) {
      document.removeEventListener('mousemove', this.updateConnectionEdit)
      document.removeEventListener('keydown', this.onConnectionKeydown, true)
      // Re-enable text selection
      document.body.style.userSelect = ''
      const edit = this.connectionEdit
      this.connectionEdit = null
      if (!edit || !edit.started) {
        return
      }
      const targetId = this.findEventIdAtPoint(event.clientX, event.clientY)
      // Drop on source or same target = cancel (no change)
      if (targetId === edit.parentId || targetId === edit.originalChildId) {
        return
      }
      // Drop on empty space = remove the connection
      if (!targetId || !this.svgGraph[targetId]) {
        const removedChildName = this.svgGraph[edit.originalChildId]?.name
        workflowApiHelpers.deleteConnectionFromEvent(edit.parentId, edit.originalChildId, this.svgGraph, this.svgGroups)
        workflowApi.updateWorkflow({
          svgGraph: this.svgGraph,
          svgLanes: this.svgLanes,
          svgGroups: this.svgGroups,
          workflow: this.workflow,
          updateNote: {
            action: 'deleted connection',
            target: removedChildName
          }
        })
        return
      }
      // Drop on event that already has this source as a parent = duplicate, just drop the old
      const newTargetNode = this.svgGraph[targetId]
      if (Array.isArray(newTargetNode.parents) && newTargetNode.parents.includes(edit.parentId)) {
        const removedChildName = this.svgGraph[edit.originalChildId]?.name
        workflowApiHelpers.deleteConnectionFromEvent(edit.parentId, edit.originalChildId, this.svgGraph, this.svgGroups)
        workflowApi.updateWorkflow({
          svgGraph: this.svgGraph,
          svgLanes: this.svgLanes,
          svgGroups: this.svgGroups,
          workflow: this.workflow,
          updateNote: {
            action: 'deleted connection',
            target: removedChildName
          }
        })
        return
      }
      // Rewire: remove old, add new
      workflowApiHelpers.deleteConnectionFromEvent(edit.parentId, edit.originalChildId, this.svgGraph, this.svgGroups)
      workflowApiHelpers.addConnectionToEvent(edit.parentId, targetId, this.svgGraph, this.svgGroups)
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'updated connection',
          target: this.svgGraph[edit.parentId]?.name
        }
      })
    },
    onConnectionKeydown (event) {
      if (event.key !== 'Escape') {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      this.cancelConnectionInteraction()
    },
    cancelConnectionInteraction () {
      document.removeEventListener('keydown', this.onConnectionKeydown, true)
      document.body.style.userSelect = ''
      if (this.connectionEdit) {
        document.removeEventListener('mousemove', this.updateConnectionEdit)
        document.removeEventListener('mouseup', this.endConnectionEdit)
        this.connectionEdit = null
      }
      if (this.connectionDrag) {
        document.removeEventListener('mousemove', this.updateConnectionDrag)
        document.removeEventListener('mouseup', this.endConnectionDrag)
        this.connectionDrag = null
        this.connectionDragJustFinished = true
        if (this.connectionDragJustFinishedTimeout) {
          clearTimeout(this.connectionDragJustFinishedTimeout)
        }
        this.connectionDragJustFinishedTimeout = setTimeout(() => {
          this.connectionDragJustFinished = false
          this.connectionDragJustFinishedTimeout = null
        }, 100)
      }
    },
    buildGhostPathFromTo (sourceId, currentX, currentY, hoveredTargetId) {
      const parentNode = this.svgGraph[sourceId]
      if (!parentNode) {
        return ''
      }
      let childNode
      if (hoveredTargetId && hoveredTargetId !== sourceId && this.svgGraph[hoveredTargetId]) {
        childNode = this.svgGraph[hoveredTargetId]
      } else {
        childNode = {
          x: currentX + 7,
          y: currentY - 48,
          type: 'bpmn:Task',
          parents: []
        }
      }
      const xDistance = childNode.x - parentNode.x
      const coordinates = svgPathHelpers.getConnectionCoordinates(parentNode, childNode)
      const breakingPoint = svgPathHelpers.getBreakingPoint(parentNode.type, childNode.type)
      if (xDistance > breakingPoint) {
        return svgPathHelpers.createForwardConnection(parentNode.type, coordinates)
      }
      if (xDistance <= 0) {
        return svgPathHelpers.createBackwardConnection(false, coordinates)
      }

      return svgPathHelpers.createVerticalLine('ghost-parent', 'ghost-child', coordinates)
    },
    isArrowHighlighted (parentId, childId) {
      if (this.dragging) {
        return false
      }
      let xSlot
      const parentNode = this.svgGraph[parentId]
      const childNode = this.svgGraph[childId]
      // only highlight arrows around the selected event
      if ([parentId, childId].includes(this.$route.params.eventId)) {
        if (parentNode.type === 'bpmn:ExclusiveGateway') {
          // the vertical line after a decision
          // is positioned directly after the desicion
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
        } else {
          // the vertical line after an event
          // is positioned directly before the child
          xSlot = positionHelpers.getSlotXFromPoint(childNode.x) - 1
        }
        let ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
        const ySlotEnd = positionHelpers.getSlotYFromPoint(childNode.y)
        const backwardsConnection = parentNode.x > childNode.x
        if (backwardsConnection) {
          // if the parent is above the child
          // the vertical line still approaches the child from one step below
          if (ySlotEnd > ySlotStart) {
            ySlotStart = ySlotEnd + 1
          }
          if (this.isColliding(xSlot, ySlotStart, ySlotEnd, backwardsConnection)) {
            return true
          }

          // the parent has a downwards arrow that also needs to be
          // checked for collisions
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
          ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
          if (this.isColliding(xSlot, ySlotStart, ySlotStart + 1, backwardsConnection)) {
            return true
          }
        } else {
          // forward connection
          if (ySlotStart !== ySlotEnd) {
            if (this.isColliding(xSlot, ySlotStart, ySlotEnd, backwardsConnection)) {
              return true
            }
          }
        }
      }
      return false
    },
    getXPointFromGroup (group) {
      return positionHelpers.getGroupXPointFromSlot(group.startSlotX)
    },
    getGroupWidth (group) {
      if (!group) {
        return 0
      }
      if (group.width && group.width > 0) {
        return group.width
      }
      return Math.max(this.diagramWidth - this.getXPointFromGroup(group), 0)
    },
    // Right edge of the visible board, from the scroll container's left edge: the container
    // clipped by the window and by the side panel, which sits fixed on top of the diagram.
    measureVisibleBoardRight () {
      const container = this.$refs.scrollContainer
      if (!container) {
        return
      }
      const containerRect = container.getBoundingClientRect()
      const windowRight = document.documentElement.clientWidth || window.innerWidth
      const panelElement = this.showSidePanel ? this.$refs.sidePanel?.$el : null
      const panelLeft = panelElement ? panelElement.getBoundingClientRect().left : Infinity
      const visibleRight = Math.min(containerRect.right, windowRight, panelLeft)

      this.visibleBoardRight = Math.max(0, visibleRight - containerRect.left)
    },
    // Keeps the ⋯ button DEFAULT_RIGHT_OFFSET from whichever right edge comes first — the
    // viewport's or the group's, which on groups wider than the screen is far off screen.
    groupActionsStyle (group) {
      const inset = this.DEFAULT_RIGHT_OFFSET
      const groupWidth = this.getGroupWidth(group)
      if (!this.visibleBoardRight || groupWidth <= 0) {
        return { right: `${inset}px` }
      }
      // Positions below are local to this group's header.
      const zoom = this.isEmbedMode ? this.embedZoom : 1
      const visibleRight = (this.bpmnScrollLeft + this.visibleBoardRight) / zoom - this.getXPointFromGroup(group)
      const parkedRight = groupWidth - inset
      // A group scrolled almost off screen leaves too little room for the button; keep it
      // inside the header there rather than let it slide out and be clipped away.
      const minRight = Math.min(parkedRight, GROUP_ACTIONS_BUTTON_WIDTH + inset)
      const right = Math.max(minRight, Math.min(parkedRight, visibleRight - inset))

      return { right: `${groupWidth - right}px` }
    },
    showLaneCellMenu (event, lane, index, group) {
      this.showLaneContextMenu(event, lane, index, group)
    },
    inspectEvent (eventId, tab = 'cards') {
      this.changeSelectedEvent(eventId)
      this.latestEventId = eventId
      this.$store.commit('setSidePanelTab', tab)
      this.$store.commit('showSidePanel', true)
    },
    closeInspectEvent (event) {
      if (this.justFinishedSelecting) {
        this.justFinishedSelecting = false
        return
      }
      if (!event || !event.shiftKey) {
        this.clearMultiSelection()
      }
      this.changeSelectedEvent(null)
      this.latestEventId = null
    },
    handleShiftClickSelection (eventId) {
      if (!eventId) return

      // Start with current selection or active event
      const currentlySelected = this.selectedEventIds.length > 0
        ? [...this.selectedEventIds]
        : (this.$route.params.eventId ? [this.$route.params.eventId] : [])

      // Toggle event in selection
      const index = currentlySelected.indexOf(eventId)
      if (index > -1) {
        currentlySelected.splice(index, 1)
      } else {
        currentlySelected.push(eventId)
      }

      // Update based on selection size
      if (currentlySelected.length <= 1) {
        this.clearMultiSelection()
        this.changeSelectedEvent(currentlySelected[0] || null)
      } else {
        this.selectedEventIds = currentlySelected
        this.selectionEvents = currentlySelected
        this.checkSelectionConnectivity(currentlySelected)
        this.updateSelectionBoundingRect()
        this.updateShowActions()

        // Navigate to first selected event if needed
        if (this.$route.params.eventId !== currentlySelected[0]) {
          this.$router.push({
            path: `/workflow/${this.$route.params.projectId}/${this.$route.params.id}/${currentlySelected[0]}`
          }).catch(() => {})
        }
      }
    },
    openAiGenerator () {
      this.$refs.generator.openSettings()
    },
    onAskForReply (event) {
      this.$refs.generator.addReplyToDiagram(event)
    },
    onUpdateCard (card) {
      const payload = {
        cardId: card.id,
        sortkey: card.sortkey,
        releaseNo: card.releaseNo,
        cardTypeId: card.cardTypeId,
        updateNote: {
          action: 'moved card',
          target: cardHelpers.getCardTarget(card)
        }
      }

      if ('tags' in card) {
        payload.tags = card.tags
      }
      if ('description' in card) {
        payload.description = card.description
      }

      workflowApi.updateCardsMutation(this.$route.params.id, [payload])
    },
    thisComponent () {
      return this
    },
    // Embed-only zoom: scales the SVG against a fixed viewBox; scroll handles overflow.
    embedZoomIn () {
      this.embedZoomTo(this.embedZoom + EMBED_ZOOM_STEP)
    },
    embedZoomOut () {
      this.embedZoomTo(this.embedZoom - EMBED_ZOOM_STEP)
    },
    embedZoomReset () {
      // "Reset" returns to the fitted view and hands control back to auto-fit.
      this.embedUserZoomed = false
      this.embedFitToScreen()
    },
    // Mouse wheel zooms the embedded board (Ctrl+wheel is avoided — the browser
    // captures it for page zoom). preventDefault stops the wheel from also scrolling.
    onEmbedWheel (e) {
      if (!this.isEmbedMode) {
        return
      }
      e.preventDefault()
      const delta = e.deltaY < 0 ? EMBED_ZOOM_STEP : -EMBED_ZOOM_STEP
      this.embedZoomTo(this.embedZoom + delta)
    },
    // Zoom anchored on the viewport centre (the SVG scales from 0,0, so it would
    // otherwise drift to the top-left). X scrolls the container, Y scrolls the window.
    embedZoomTo (nextZoom) {
      const zoom = Math.max(EMBED_ZOOM_MIN, Math.min(EMBED_ZOOM_MAX, Math.round(nextZoom * 100) / 100))
      if (zoom === this.embedZoom) {
        return
      }
      const oldZoom = this.embedZoom
      const container = this.$refs.scrollContainer
      const svg = this.$refs.flowchart

      // Point under the viewport centre, captured before zooming.
      let centreX = 0
      if (container) {
        centreX = (container.scrollLeft + container.clientWidth / 2) / oldZoom
      }
      let centreY = 0
      let svgTop = 0
      if (svg) {
        const rect = svg.getBoundingClientRect()
        svgTop = rect.top + window.scrollY
        centreY = ((window.scrollY + window.innerHeight / 2) - svgTop) / oldZoom
      }

      this.embedUserZoomed = true
      this.embedZoom = zoom

      // Re-anchor scroll after the SVG resizes (negative targets clamp to 0).
      this.$nextTick(() => {
        if (container) {
          container.scrollLeft = centreX * zoom - container.clientWidth / 2
        }
        if (svg) {
          window.scrollTo(window.scrollX, svgTop + centreY * zoom - window.innerHeight / 2)
        }
      })
    },
    // Show the embed controls while the pointer is anywhere in the frame (board or
    // empty area), hide once it leaves — set via document-level listeners.
    showEmbedControls () {
      if (!this.embedControlsVisible) {
        this.embedControlsVisible = true
      }
    },
    hideEmbedControls () {
      if (this.embedControlsVisible) {
        this.embedControlsVisible = false
      }
    },
    // Position of one label in the frozen embed group-header bar: tracks the columns
    // (offset by horizontal scroll) and scales with zoom, mirroring the SVG headers.
    embedGroupHeaderStyle (group) {
      const x = this.getXPointFromGroup(group)
      const width = group.width > 0 ? group.width : (this.diagramWidth - x)
      return {
        left: (x * this.embedZoom - this.bpmnScrollLeft) + 'px',
        width: (width * this.embedZoom) + 'px',
        fontSize: (14 * this.embedZoom) + 'px'
      }
    },
    async checkClipboardValidity () {
      this.clipboardHasValidJson = await copyPasteHelpers.checkClipboardValidity()
    },
    async showLaneContextMenu (event, lane, index, groupOverride = null) {
      if (this.$store.state.isWorkflowDisabled) {
        return
      }

      // Check clipboard validity for paste option
      await this.checkClipboardValidity()

      // Determine which group the click is in
      const clickX = event.clientX + this.$refs.scrollContainer.scrollLeft - 90
      const group = groupOverride || positionHelpers.getGroupFromXCoordinate(clickX, this.svgGroups)

      // Store the old show state
      const wasOpen = this.laneContextMenu.show

      // Update the context menu position and data
      this.laneContextMenu.activator = { x: event.clientX, y: event.clientY }
      this.laneContextMenu.laneId = lane.id
      this.laneContextMenu.laneIndex = index
      this.laneContextMenu.group = group || null

      // If menu is already open, update position with minimal flicker
      if (wasOpen) {
        // Close and reopen instantly using requestAnimationFrame for smooth transition
        // The :transition="false" on v-menu prevents animation flicker
        this.laneContextMenu.show = false
        // Use requestAnimationFrame to ensure the close happens in the next frame
        requestAnimationFrame(() => {
          // Reopen immediately in the same frame cycle
          this.laneContextMenu.show = true
          this.$nextTick(() => {
            if (this.$refs.laneContextMenuActivator) {
              this.$refs.laneContextMenuActivator.click()
            }
          })
        })
        return
      }

      // Trigger menu in nextTick to ensure activator is positioned
      this.$nextTick(() => {
        this.laneContextMenu.show = true
        // Programmatically click the activator to trigger the menu
        if (this.$refs.laneContextMenuActivator) {
          this.$refs.laneContextMenuActivator.click()
        }
      })
    },
    handleContextMenuAction (action, groupId = null) {
      const { laneId, laneIndex, group } = this.laneContextMenu
      this.laneContextMenu.show = false

      switch (action) {
        case 'paste':
          // Pass the target laneId and groupId from context menu
          this.$emit('on-paste', {
            targetLaneId: laneId,
            targetGroupId: group?.id || null
          })
          break
        case 'add-start-point':
          this.addStartingPointToLaneAndGroup(laneId, group?.id || null)
          break
        case 'add-lane-above':
          this.addLaneRelativeTo(laneId, 'above')
          break
        case 'add-lane-below':
          this.addLaneRelativeTo(laneId, 'below')
          break
        case 'add-group-before':
          if (group) {
            this.addIntermediateGroup(group, 'before')
          } else {
            this.addGroup()
          }
          break
        case 'add-group-after':
          if (group) {
            this.addIntermediateGroup(group, 'after')
          } else {
            this.addGroup()
          }
          break
        case 'ungroup':
          if (group) {
            this.ungroup(group)
          }
          break
        case 'delete-group':
          if (group) {
            this.deleteGroup(group)
          }
          break
        case 'delete-lane':
          this.deleteLane(laneId, laneIndex)
          break
      }
    },
    // Returns the group adjacent to the given group on the given side, or null.
    adjacentGroup (group, side) {
      const index = this.sortedGroups.findIndex((g) => g.id === group.id)
      if (index === -1) {
        return null
      }

      return this.sortedGroups[side === 'left' ? index - 1 : index + 1] || null
    },
    // Switches the group with its neighbour. The helper always switches with the
    // right neighbour, so a left switch is driven from the left neighbour.
    switchGroup (group, side) {
      const leftGroup = side === 'left' ? this.adjacentGroup(group, 'left') : group
      if (!leftGroup) {
        return
      }

      workflowApi.switchGroups(leftGroup, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    // ----- Group drag-reorder -----
    // A header press becomes a reorder once the pointer moves, else a click edits the name.
    onGroupHeaderMouseDown (event, group) {
      if (this.editingGroupId === group.id || event.button !== 0 || this.$store.state.isWorkflowDisabled) {
        return
      }
      this.groupDragCandidate = { x: event.clientX, y: event.clientY, group }
      document.addEventListener('mousemove', this.onGroupHeaderMouseMove)
      document.addEventListener('mouseup', this.onGroupHeaderMouseUp)
    },
    onGroupHeaderMouseMove (event) {
      if (!this.groupDragCandidate) {
        return
      }
      const moved = Math.abs(event.clientX - this.groupDragCandidate.x) +
        Math.abs(event.clientY - this.groupDragCandidate.y)
      if (moved <= 4) {
        return
      }
      const group = this.groupDragCandidate.group
      const startClientX = this.groupDragCandidate.x
      this.clearGroupDragCandidate()
      event.preventDefault()
      const selection = window.getSelection && window.getSelection()
      if (selection && selection.removeAllRanges) {
        selection.removeAllRanges()
      }
      this.startGroupReorder(group, startClientX)
    },
    onGroupHeaderMouseUp () {
      if (!this.groupDragCandidate) {
        return
      }
      const group = this.groupDragCandidate.group
      this.clearGroupDragCandidate()
      this.startGroupEditing(group)
    },
    clearGroupDragCandidate () {
      document.removeEventListener('mousemove', this.onGroupHeaderMouseMove)
      document.removeEventListener('mouseup', this.onGroupHeaderMouseUp)
      this.groupDragCandidate = null
    },
    startGroupEditing (group) {
      if (this.$store.state.isWorkflowDisabled) {
        return
      }
      this.editingGroupId = group.id
      this.$nextTick(() => {
        const node = this.$refs[`groupName-${group.id}`]?.[0]
        if (!node) {
          return
        }
        node.focus()
        const selection = window.getSelection && window.getSelection()
        if (!selection) {
          return
        }
        const range = document.createRange()
        range.selectNodeContents(node)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      })
    },
    onGroupNameBlur (event, group) {
      this.editingGroupId = null
      this.updateGroupName(event, group)
    },
    startGroupReorder (group, startClientX) {
      const baseGroups = safeStructuredClone(this.svgGroups)
      const baseGraph = safeStructuredClone(this.svgGraph)
      const baseOrder = Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX).map((g) => g.id)
      const fromIndex = baseOrder.indexOf(group.id)
      const draggedGroup = baseGroups[group.id]
      const svgEl = this.$refs.flowchart || document.getElementById('flowchart')
      const rectLeft = svgEl ? svgEl.getBoundingClientRect().left : 0
      // For the last group (width -1), use its content extent so its midpoint isn't off-screen.
      const rightmostEventX = Math.max(0, ...Object.values(this.svgGraph).map((node) => node.x))
      const widthOf = (g) => groupWidthForDrop(g, rightmostEventX)

      // Ghost width = slot span, not rendered width (first/last groups render wider via the offset).
      const sortedBase = Object.values(baseGroups).sort((a, b) => a.startSlotX - b.startSlotX)
      const nextOfDragged = sortedBase[fromIndex + 1]
      const draggedSpan = nextOfDragged
        ? nextOfDragged.startSlotX - draggedGroup.startSlotX
        : Math.max(
          1,
          Math.max(draggedGroup.startSlotX, ...Object.values(baseGraph).map((node) => positionHelpers.getSlotXFromPoint(node.x))) - draggedGroup.startSlotX + 1
        )
      const baseWidth = draggedSpan * 160
      // Extra width the first group gets from the lane-width offset (its left edge abuts the lane).
      const firstGroupExtra = positionHelpers.getGroupXPointFromSlot(1) - positionHelpers.getGroupXPointFromSlot(0) - 160

      // Snapshot at drag start so thresholds stay fixed while the diagram reflows underneath.
      this.groupReorder = {
        active: true,
        groupId: group.id,
        fromIndex,
        dropIndex: fromIndex,
        grabOffset: (startClientX - rectLeft) - draggedGroup.x,
        draggedWidth: baseWidth,
        firstGroupExtra,
        ghostWidth: baseWidth + (fromIndex === 0 ? firstGroupExtra : 0),
        draggedName: draggedGroup.name,
        ghostLeft: draggedGroup.x,
        // Other groups: horizontal midpoint + whether each starts left of the dragged one.
        others: baseOrder
          .map((id, i) => ({ id, i }))
          .filter((entry) => entry.id !== group.id)
          .map((entry) => ({
            id: entry.id,
            mid: baseGroups[entry.id].x + widthOf(baseGroups[entry.id]) / 2,
            before: entry.i < fromIndex
          })),
        baseGroups,
        baseGraph,
        baseOrder
      }
      document.body.style.userSelect = 'none'
      document.body.classList.add('group-reordering')
      document.addEventListener('mousemove', this.onGroupReorderMove)
      document.addEventListener('mouseup', this.onGroupReorderEnd, { once: true })
    },
    onGroupReorderMove (event) {
      if (!this.groupReorder.active) {
        return
      }
      const svgEl = this.$refs.flowchart || document.getElementById('flowchart')
      if (!svgEl) {
        return
      }
      const rectLeft = svgEl.getBoundingClientRect().left
      const cursorX = event.clientX - rectLeft
      const leftEdge = cursorX - this.groupReorder.grabOffset
      const rightEdge = leftEdge + this.groupReorder.draggedWidth
      const insertionIndex = computeDropIndex(leftEdge, rightEdge, this.groupReorder.others)
      const { ghostLeft, ghostWidth } = computeGhost(
        leftEdge,
        this.groupReorder.draggedWidth,
        this.groupReorder.firstGroupExtra,
        insertionIndex
      )
      this.groupReorder.ghostLeft = ghostLeft
      this.groupReorder.ghostWidth = ghostWidth
      if (insertionIndex !== this.groupReorder.dropIndex) {
        this.groupReorder.dropIndex = insertionIndex
        this.previewGroupReorder()
      }
    },
    // Live frontend-only preview: reorder from the drag-start snapshot to the current target index.
    previewGroupReorder () {
      const { groupId, dropIndex, baseGroups, baseGraph } = this.groupReorder
      const svgGroups = safeStructuredClone(baseGroups)
      const svgGraph = safeStructuredClone(baseGraph)
      workflowApiHelpers.reorderGroup(groupId, dropIndex, svgGraph, svgGroups)
      this.svgGroups = svgGroups
      this.svgGraph = svgGraph
    },
    onGroupReorderEnd () {
      document.removeEventListener('mousemove', this.onGroupReorderMove)
      document.body.style.userSelect = ''
      document.body.classList.remove('group-reordering')
      const reorder = this.groupReorder
      this.groupReorder = {
        active: false,
        groupId: null,
        fromIndex: -1,
        dropIndex: -1,
        ghostLeft: 0,
        ghostWidth: 0,
        draggedWidth: 0,
        firstGroupExtra: 0,
        draggedName: ''
      }
      if (!reorder.active) {
        return
      }
      if (reorder.dropIndex === reorder.fromIndex) {
        // No change: restore the drag-start snapshot.
        this.svgGroups = safeStructuredClone(reorder.baseGroups)
        this.svgGraph = safeStructuredClone(reorder.baseGraph)

        return
      }

      // svgGraph/svgGroups already hold the previewed final state.
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'reordered group',
          target: reorder.draggedName
        }
      })
    },
    addStartingPointToLaneAndGroup (laneId, groupId) {
      this.addShape(
        {
          laneId,
          groupId: (!groupId || !this.sortedGroups.length || groupId === this.sortedGroups[0].id)
            ? null // null is for first group
            : groupId
        },
        { parentEventId: 'start' }
      )
    },
    addStartingPointToLane (laneId) {
      const groupId = this.sortedGroups.length > 0 ? this.sortedGroups[0].id : null
      this.addStartingPointToLaneAndGroup(laneId, groupId)
    },
    resetDiagram () {
      this.endDrag(false)
      this.svgGraph = JSON.parse(JSON.stringify(this.svgGraphData))
      this.svgLanes = JSON.parse(JSON.stringify(this.svgLanesData))
      this.svgGroups = JSON.parse(JSON.stringify(this.svgGroupsData))

      this.$store.commit('updateSvgGraph', JSON.parse(JSON.stringify(this.svgGraphData)))
      this.$store.commit('updateSvgLanes', JSON.parse(JSON.stringify(this.svgLanesData)))
    },
    computeEventBadgesData (node) {
      const badges = []

      for (const cardType of this.cardTypes) {
        const cardsToShow = node.cards?.filter((req) => {
          return !req.done &&
            req.cardType &&
            req.cardType.id === cardType.id &&
            (!this.showReleaseNo || this.showReleaseNo?.length === 0 || this.showReleaseNo?.includes(req.releaseNo))
        })

        const count = cardsToShow?.length || 0

        if (count > 0) {
          badges.push({ ...cardType, count })
        }
      }

      return { badges, restCardsCount: badges.slice(5).reduce((a, b) => a + b.count, 0) }
    },
    computeCardsByEventId () {
      if (!this.events) { return {} }
      return this.events.reduce((accum, event) => {
        accum[event.id] = event.cards?.filter((req) =>
          !this.showReleaseNo ||
          this.showReleaseNo.length === 0 ||
          this.showReleaseNo.includes(req.releaseNo)
        ) || []
        return accum
      }, {})
    },
    updateShowActions () {
      const canShow = !!this.primarySelectedEvent &&
        !this.$store.state.isWorkflowDisabled &&
        !this.dragging &&
        !this.isSelectionDragging &&
        !this.isSelecting &&
        !this.editingBackwardLabel
      this.showActions = canShow
    },
    onBackwardLabelFocus () {
      this.editingBackwardLabel = true
      this.showActions = false
    },
    onBackwardLabelBlur (ev, node, parentId) {
      this.editingBackwardLabel = false
      this.blurOnConditionLabels(ev, node, parentId)
      this.updateShowActions()
    },
    startSelection (event) {
      const container = this.$refs.scrollContainer
      if (!container) {
        return
      }
      const rect = container.getBoundingClientRect()
      const startX = event.clientX - rect.left + container.scrollLeft
      const startY = event.clientY - rect.top + container.scrollTop
      this.isSelecting = true
      this.selectionEvents = []
      this.selectedEventIds = []
      this.selectionBoundingRect = null
      this.invalidSelectionMessage = null
      this.selectionCapability = 'full'
      this.selectionStart = { x: startX, y: startY }
      this.selectionRect = {
        x: startX,
        y: startY,
        width: 0,
        height: 0
      }

      // Disable text selection during multi-selection drag
      document.body.style.userSelect = 'none'

      this.updateShowActions()
    },
    updateSelection (event) {
      if (!this.isSelecting || !this.selectionStart) {
        return
      }
      const container = this.$refs.scrollContainer
      if (!container) {
        return
      }
      const rect = container.getBoundingClientRect()
      const currentX = event.clientX - rect.left + container.scrollLeft
      const currentY = event.clientY - rect.top + container.scrollTop
      const left = Math.min(currentX, this.selectionStart.x)
      const top = Math.min(currentY, this.selectionStart.y)
      const width = Math.abs(currentX - this.selectionStart.x)
      const height = Math.abs(currentY - this.selectionStart.y)
      this.selectionRect = { x: left, y: top, width, height }
      this.calculateSelectionEvents()
    },
    finishSelection () {
      if (this.isSelecting) {
        this.isSelecting = false
      }

      // Re-enable text selection
      document.body.style.userSelect = ''

      const hadSelectionRect = !!this.selectionRect
      if (this.selectionRect && this.selectionRect.width < 2 && this.selectionRect.height < 2) {
        this.selectionRect = null
        this.selectionEvents = []
      } else if (this.selectionRect) {
        this.calculateSelectionEvents(true)
      } else {
        this.selectionEvents = []
      }
      this.selectionStart = null
      if (hadSelectionRect) {
        this.selectionRect = null
      }
      if (this.selectionEvents.length > 0) {
        this.selectedEventIds = [...this.selectionEvents]

        // Set flag to prevent click handler from immediately clearing selection
        this.justFinishedSelecting = true
      } else if (this.$route.params.eventId) {
        this.selectedEventIds = [this.$route.params.eventId]
      } else {
        this.selectedEventIds = []
      }
      this.updateSelectionBoundingRect()
      this.updateShowActions()
    },
    calculateSelectionEvents (isFinal = false) {
      if (!this.selectionRect) {
        if (this.selectionEvents.length > 0) {
          this.selectionEvents = []
          // Clear selectedEventIds during dragging for real-time visual feedback
          if (this.isSelecting) {
            this.selectedEventIds = []
          }
        }
        return
      }
      const container = this.$refs.scrollContainer
      const flowchart = this.$refs.flowchart
      if (!container || !flowchart) {
        return
      }
      const containerRect = container.getBoundingClientRect()
      const selectedIds = []
      Object.values(this.svgGraph || {}).forEach(node => {
        const nodeElement = document.getElementById(`r-${node.id}`) ||
          document.getElementById(`p-${node.id}`) ||
          document.getElementById(node.id)
        if (!nodeElement) {
          return
        }
        const nodeRect = nodeElement.getBoundingClientRect()
        const rect = {
          x: nodeRect.left - containerRect.left + container.scrollLeft,
          y: nodeRect.top - containerRect.top + container.scrollTop,
          width: nodeRect.width,
          height: nodeRect.height
        }
        if (this.rectanglesOverlap(this.selectionRect, rect)) {
          selectedIds.push(node.id)
        }
      })
      const uniqueSelected = [...new Set(selectedIds)]

      let changed = uniqueSelected.length !== this.selectionEvents.length
      if (!changed) {
        const previousSet = new Set(this.selectionEvents)
        changed = uniqueSelected.some(id => !previousSet.has(id))
      }

      if (changed) {
        this.selectionEvents = uniqueSelected
        // Update selectedEventIds during dragging for real-time visual feedback
        this.selectedEventIds = [...uniqueSelected]
        // Check connectivity during dragging to update visual feedback
        this.checkSelectionConnectivity(uniqueSelected)
        if (uniqueSelected.length > 0 && isFinal) {
          this.evaluateSelectionConnectivity()
        }
      } else if (isFinal) {
        if (uniqueSelected.length > 0) {
          this.evaluateSelectionConnectivity()
          this.updateSelectionBoundingRect()
        } else {
          this.selectionBoundingRect = null
        }
      }
    },
    checkSelectionConnectivity (selectedIds) {
      const { capability, message } = this.computeSelectionCapability(selectedIds, this.svgGraph, this.svgGroups)
      this.selectionCapability = capability
      this.invalidSelectionMessage = message
    },
    evaluateSelectionConnectivity () {
      if (!this.selectionEvents?.length) {
        return
      }
      // Keep the selection and classify it: ones that can't be reordered stay as
      // 'verticalOnly' (lane change still works) rather than being discarded.
      const ids = [...new Set(this.selectionEvents)]
      this.selectedEventIds = ids
      this.selectionEvents = ids
      this.checkSelectionConnectivity(ids)
      this.updateSelectionBoundingRect()
      this.updateShowActions()
    },
    clearMultiSelection () {
      // Don't clear if we just finished dragging a multi-selection
      if (this.justFinishedDragging && this.selectedEventIds?.length > 1) {
        this.justFinishedDragging = false
        return
      }

      this.selectionEvents = []
      this.selectedEventIds = []
      this.selectionBoundingRect = null
      this.selectionRect = null
      this.selectionCapability = 'full'
      this.invalidSelectionMessage = null
      this.updateShowActions()
    },
    updateSelectionBoundingRect () {
      if (this.isSelectionDragging) {
        return
      }
      if (!Array.isArray(this.selectedEventIds) || this.selectedEventIds.length <= 1) {
        this.selectionBoundingRect = null
        return
      }
      this.$nextTick(() => {
        const boundingRect = this.computeSelectionBoundingRect(this.selectedEventIds)
        this.selectionBoundingRect = boundingRect
      })
    },
    computeSelectionBoundingRect (ids) {
      const container = this.$refs.scrollContainer
      if (!container || !Array.isArray(ids) || ids.length <= 1) {
        return null
      }
      const containerRect = container.getBoundingClientRect()
      let minX = Number.POSITIVE_INFINITY
      let minY = Number.POSITIVE_INFINITY
      let maxX = Number.NEGATIVE_INFINITY
      let maxY = Number.NEGATIVE_INFINITY
      ids.forEach(id => {
        const nodeElement = document.getElementById(`r-${id}`) ||
          document.getElementById(`p-${id}`) ||
          document.getElementById(id)
        if (!nodeElement) {
          return
        }
        const nodeRect = nodeElement.getBoundingClientRect()
        const x = nodeRect.left - containerRect.left + container.scrollLeft
        const y = nodeRect.top - containerRect.top + container.scrollTop
        const right = x + nodeRect.width
        const bottom = y + nodeRect.height
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (right > maxX) maxX = right
        if (bottom > maxY) maxY = bottom
      })
      if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
        return null
      }
      return {
        x: minX,
        y: minY,
        width: Math.max(0, maxX - minX),
        height: Math.max(0, maxY - minY)
      }
    },
    handleSelectionBoundsMousedown (event) {
      // If shift key is pressed, we want to allow shift+click on underlying elements
      if (event.shiftKey) {
        // Temporarily hide the overlay to find the element underneath
        const overlay = event.target
        const originalPointerEvents = overlay.style.pointerEvents
        overlay.style.pointerEvents = 'none'

        // Find the element at the mouse position
        const elementBelow = document.elementFromPoint(event.clientX, event.clientY)

        // Restore pointer events
        overlay.style.pointerEvents = originalPointerEvents

        // If we found an element, dispatch a click event to it with shift key
        if (elementBelow) {
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            shiftKey: true,
            clientX: event.clientX,
            clientY: event.clientY,
            button: event.button
          })
          elementBelow.dispatchEvent(clickEvent)
        }

        event.preventDefault()
        event.stopPropagation()
        return
      }

      // Otherwise, start the selection drag
      this.startSelectionDrag(event)
    },
    startSelectionDrag (event) {
      if (event?.button !== 0 || this.isSelectionDragging || !this.selectedEventIds.length || !this.selectionBoundingRect) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      this.isSelectionDragging = true
      this.dragging = true
      this.disableNormalScroll()
      this.selectionDragStart = { x: event.clientX, y: event.clientY }
      this.selectionDragInitialRect = { ...this.selectionBoundingRect }
      this.selectionDragInitialNodes = this.selectedEventIds
        .map(id => ({
          id,
          x: this.svgGraph?.[id]?.x ?? 0,
          y: this.svgGraph?.[id]?.y ?? 0
        }))

      // Find input and output anchor nodes for switching logic
      const { inputAnchor, outputAnchor } = dragFunctions.getMultiSelectionAnchors(this.selectedEventIds, this.svgGraph)
      if (inputAnchor) {
        this.multiSelectionInputAnchorId = inputAnchor.id
        this.multiSelectionInputSlotX = positionHelpers.getSlotXFromId(inputAnchor.id, this.svgGraph)
      }
      if (outputAnchor) {
        this.multiSelectionOutputAnchorId = outputAnchor.id
        this.multiSelectionOutputSlotX = positionHelpers.getSlotXFromId(outputAnchor.id, this.svgGraph)
      }

      document.addEventListener('mousemove', this.handleSelectionDrag)
      this.updateShowActions()
    },
    handleSelectionDrag (event) {
      if (!this.isSelectionDragging || !this.selectionDragInitialRect) {
        return
      }
      event.preventDefault()
      // A vertical-only selection can change lanes but not reorder, so lock the X axis.
      const verticalOnly = this.selectionCapability === 'verticalOnly'
      const deltaX = verticalOnly ? 0 : event.clientX - this.selectionDragStart.x
      const deltaY = event.clientY - this.selectionDragStart.y
      this.selectionBoundingRect = {
        x: this.selectionDragInitialRect.x + deltaX,
        y: this.selectionDragInitialRect.y + deltaY,
        width: this.selectionDragInitialRect.width,
        height: this.selectionDragInitialRect.height
      }
      this.selectionDragInitialNodes.forEach(nodeState => {
        const node = this.svgGraph?.[nodeState.id]
        if (node) {
          node.x = nodeState.x + deltaX
          node.y = nodeState.y + deltaY
        }
      })

      // Detect slot changes for switching logic (similar to single event drag)
      const selectedSet = new Set(this.selectedEventIds)

      // Horizontal slot-switching is meaningless for a vertical-only (lane change) drag.
      if (!verticalOnly && this.multiSelectionInputAnchorId) {
        const inputAnchor = this.svgGraph?.[this.multiSelectionInputAnchorId]
        const outputAnchor = this.svgGraph?.[this.multiSelectionOutputAnchorId]
        if (inputAnchor) {
          const { slotX: inputSlotX } = positionHelpers.getSlotsFromNode(inputAnchor, this.svgGraph)
          const { slotX: outputSlotX } = positionHelpers.getSlotsFromNode(outputAnchor, this.svgGraph)

          if (inputSlotX === this.multiSelectionInputSlotX) {
            return
          }

          // Dragging LEFT by one slot - check if should switch with external parent of input anchor
          if (inputSlotX === this.multiSelectionInputSlotX - 1) {
            // If the outpuGroup is different from the beforeGroup, adjust the group size
            const inputGroup = positionHelpers.getGroupFromSlotX(inputSlotX, this.svgGroups)
            const beforeGroup = positionHelpers.getGroupFromSlotX(this.multiSelectionInputSlotX, this.svgGroups)

            if (inputGroup && inputGroup !== beforeGroup) {
              this.adjustGroupSizeForMultiSelection('left', inputGroup, inputAnchor, outputAnchor, selectedSet)
              if (inputAnchor.groupId) {
                outputAnchor.childIds?.forEach(childId => {
                  const child = this.svgGraph?.[childId]
                  if (!child) return
                  const { slotX: childSlotX } = positionHelpers.getSlotsFromNode(child, this.svgGraph)
                  const childGroup = positionHelpers.getGroupFromSlotX(childSlotX, this.svgGroups)
                  if (inputAnchor.groupId === childGroup.id) {
                    child.groupId = inputAnchor.groupId
                  }
                })
              }
              inputAnchor.groupId = null
              this.alignBranchesPreservingSelection(selectedSet)
            }

            // Check if the switching the position of the external parent is needed, if so, do it
            const externalParentId = inputAnchor.parents.find(p => p !== 'start' && !selectedSet.has(p))
            if (externalParentId) {
              const externalParent = this.svgGraph?.[externalParentId]
              if (externalParent) {
                const { slotX: externalParentSlotX } = positionHelpers.getSlotsFromNode(externalParent, this.svgGraph)
                const externalParentGroup = positionHelpers.getGroupFromSlotX(externalParentSlotX, this.svgGroups)
                const inputGroup = positionHelpers.getGroupFromSlotX(inputSlotX, this.svgGroups)

                if (externalParentGroup === inputGroup && externalParentSlotX === inputSlotX) {
                  dragFunctions.switchSelectionWithParent(this.multiSelectionInputAnchorId, this.multiSelectionOutputAnchorId, selectedSet, this.svgGraph, this.svgGroups)
                }
              }
            }

            this.multiSelectionInputSlotX -= 1
            this.multiSelectionOutputSlotX -= 1
          }

          // Dragging RIGHT by one slot - check if should switch with external child of output anchor
          if (outputSlotX === this.multiSelectionOutputSlotX + 1) {
            // If the outpuGroup is different from the beforeGroup, adjust the group size
            const outputGroup = positionHelpers.getGroupFromSlotX(outputSlotX, this.svgGroups)
            const beforeGroup = positionHelpers.getGroupFromSlotX(this.multiSelectionOutputSlotX, this.svgGroups)

            if (outputGroup !== beforeGroup) {
              this.adjustGroupSizeForMultiSelection('right', outputGroup, inputAnchor, outputAnchor, selectedSet)
              inputAnchor.groupId = outputGroup.id
              outputAnchor.childIds?.forEach(childId => {
                const child = this.svgGraph?.[childId]
                if (child && child.groupId === outputGroup.id) {
                  child.groupId = null
                }
              })
              this.alignBranchesPreservingSelection(selectedSet)
            }

            // Check if the switching the position of the external child is needed, if so, do it
            const externalChildId = outputAnchor.childIds?.find(childId => !selectedSet.has(childId))
            if (externalChildId) {
              const externalChild = this.svgGraph?.[externalChildId]
              if (externalChild) {
                const { slotX: externalChildSlotX } = positionHelpers.getSlotsFromNode(externalChild, this.svgGraph)
                const externalChildGroup = positionHelpers.getGroupFromSlotX(externalChildSlotX, this.svgGroups)

                if (externalChildGroup === outputGroup && externalChildSlotX === outputSlotX) {
                  dragFunctions.switchSelectionWithChild(this.multiSelectionInputAnchorId, this.multiSelectionOutputAnchorId, selectedSet, this.svgGraph, this.svgGroups)
                }
              }
            }

            this.multiSelectionInputSlotX += 1
            this.multiSelectionOutputSlotX += 1
          }
        }
      }
    },
    stopSelectionDrag (commitUpdate = true) {
      if (!this.isSelectionDragging) {
        return
      }
      document.removeEventListener('mousemove', this.handleSelectionDrag)
      this.enableNormalScroll()
      const deltaX = (this.selectionBoundingRect?.x ?? 0) - (this.selectionDragInitialRect?.x ?? 0)
      const deltaY = (this.selectionBoundingRect?.y ?? 0) - (this.selectionDragInitialRect?.y ?? 0)
      const moved = Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1
      this.isSelectionDragging = false
      this.dragging = false
      this.selectionDragStart = null
      this.selectionDragInitialRect = null

      // If we moved, set flag to prevent click handlers from clearing selection
      if (moved && commitUpdate) {
        this.justFinishedDragging = true
      }

      if (!commitUpdate || !moved) {
        this.selectionDragInitialNodes = []
        this.updateSelectionBoundingRect()
        this.updateShowActions()
        return
      }
      const updatedIds = new Set(this.selectedEventIds || [])

      // Update group assignments for selected nodes and their external children
      this.updateGroupAssignmentsAfterMultiSelectionDrag(updatedIds)

      // Use the new multi-selection snap function to handle positioning as a group
      this.snapMultiSelectionToGrid(this.selectedEventIds)

      const svgGraphClone = safeStructuredClone(this.svgGraph)
      if (this.showReleaseNo) {
        Object.keys(svgGraphClone).forEach((key) => {
          svgGraphClone[key].cards = undefined
        })
      }
      workflowApi.updateWorkflow({
        svgGraph: svgGraphClone,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'moved selection',
          target: `${updatedIds.size} item${updatedIds.size === 1 ? '' : 's'}`
        }
      })

      this.selectionDragInitialNodes = []
      this.multiSelectionSlotX = 0
      this.multiSelectionInputAnchorId = null
      this.multiSelectionOutputAnchorId = null
      this.updateSelectionBoundingRect()
      this.updateShowActions()
    },
    adjustGroupSizeForMultiSelection (direction, targetGroup, inputAnchor, outputAnchor, selectedSet) {
      // Calculate the width of selected shapes using helper function
      const multiShapeSlotSize = dragFunctions.getMultiSelectionSlotWidth(selectedSet, this.svgGraph)

      let checkSlotX = null
      let externalEventId = null

      if (direction === 'left') {
        // Consider inputAnchor
        const externalParent = this.svgGraph?.[inputAnchor.parents.find(p => p !== 'start' && !selectedSet.has(p))]

        // Calculate externalParentSlotX: either from actual parent or virtual parent at group boundary
        const externalParentSlotX = externalParent
          ? positionHelpers.getSlotXFromPoint(externalParent.x)
          : targetGroup.startSlotX - 1

        const externalParentGroup = positionHelpers.getGroupFromSlotX(externalParentSlotX, this.svgGroups)

        // Check if inputAnchor and externalParent are in the same group
        if (externalParentGroup && targetGroup.id === externalParentGroup.id) {
          // Same group: Calculate externalParentSlotX + multiShapeSlotSize
          checkSlotX = externalParentSlotX + multiShapeSlotSize
        } else {
          // Different groups: check if target group width < multiShapeSlotSize
          const groupWidth = workflowApiHelpers.getGroupWidthBySlot(targetGroup.id, this.svgGraph, this.svgGroups)
          if (groupWidth < multiShapeSlotSize) {
            checkSlotX = targetGroup.startSlotX + multiShapeSlotSize - 1
          }
        }
      } else if (direction === 'right') {
        // Consider outputAnchor
        const outputSlotX = positionHelpers.getSlotXFromId(outputAnchor.id, this.svgGraph)
        targetGroup = positionHelpers.getGroupFromSlotX(outputSlotX, this.svgGroups)

        // Get external child
        externalEventId = outputAnchor.childIds?.find(childId => !selectedSet.has(childId))

        if (externalEventId && targetGroup) {
          const externalChildSlotX = positionHelpers.getSlotXFromId(externalEventId, this.svgGraph)
          const externalChildGroup = positionHelpers.getGroupFromSlotX(externalChildSlotX, this.svgGroups)

          // Check if outputAnchor and externalChild are in the same group
          if (externalChildGroup && targetGroup.id === externalChildGroup.id) {
            // Find the most right descendant of externalChild
            const mostRightDescendant = workflowApiHelpers.getMostRightDescendantInGroup(
              externalEventId,
              externalChildGroup.id,
              this.svgGraph,
              this.svgGroups
            )

            const mostRightSlotX = mostRightDescendant
              ? positionHelpers.getSlotXFromPoint(mostRightDescendant.x)
              : externalChildSlotX

            // Calculate: mostRightSlotX + multiShapeSlotSize
            checkSlotX = mostRightSlotX + multiShapeSlotSize
          } else {
            // Different groups - check if target group width < multiShapeSlotSize
            const groupWidth = workflowApiHelpers.getGroupWidthBySlot(targetGroup.id, this.svgGraph, this.svgGroups)
            if (groupWidth < multiShapeSlotSize) {
              checkSlotX = targetGroup.startSlotX + multiShapeSlotSize - 1
            }
          }
        }
      }

      if (!targetGroup || checkSlotX === null) {
        return false
      }

      // Get sorted groups to find adjacent groups
      const sortedGroups = Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
      const targetGroupIndex = sortedGroups.findIndex(g => g.id === targetGroup.id)
      const nextGroup = sortedGroups[targetGroupIndex + 1]
      const groupEndSlotX = nextGroup ? nextGroup.startSlotX - 1 : Infinity

      // Check if we need to extend the group
      if (checkSlotX > groupEndSlotX && nextGroup) {
        const shiftAmount = checkSlotX - groupEndSlotX

        // Extend the group using moveGroupRight - same as single event movement
        dragFunctions.moveGroupRight(nextGroup, this.svgGroups, shiftAmount)

        return true
      }

      return false
    },
    updateGroupAssignmentsAfterMultiSelectionDrag (selectedSet) {
      // Get input and output anchors
      const inputAnchor = this.svgGraph?.[this.multiSelectionInputAnchorId]
      const outputAnchor = this.svgGraph?.[this.multiSelectionOutputAnchorId]

      if (!inputAnchor && !outputAnchor) {
        return
      }

      // Helper function to determine if a node should have groupId
      // 1. Update inputAnchor's groupId
      if (inputAnchor) {
        inputAnchor.groupId = workflowApiHelpers.shouldHaveGroupId(inputAnchor, this.svgGraph, this.svgGroups)
      }

      // 2. Update groupIds for external children of the output anchor
      if (outputAnchor?.childIds) {
        outputAnchor.childIds.forEach(childId => {
          // Only process external children (not in selection)
          if (!selectedSet.has(childId)) {
            const childNode = this.svgGraph?.[childId]
            if (childNode && childNode.groupId == null) {
              childNode.groupId = workflowApiHelpers.shouldHaveGroupId(childNode, this.svgGraph, this.svgGroups)
            }
          }
        })
      }

      // Note: Other nodes in the selection keep their existing groupIds unchanged
    },
    alignBranchesPreservingSelection (selectedSet = null) {
      // Use provided selectedSet or default to component's selectedEventIds
      const nodesToPreserve = selectedSet || new Set(this.selectedEventIds)

      // Save positions of nodes being dragged
      const savedPositions = new Map()
      nodesToPreserve.forEach(id => {
        const node = this.svgGraph[id]
        if (node) {
          savedPositions.set(id, { x: node.x, y: node.y })
        }
      })

      // Align all branches (this correctly shifts external children by shiftAmount)
      workflowApiHelpers.alignBranches(this.svgGraph, this.svgGroups)

      // Restore dragged nodes' positions to maintain smooth drag
      nodesToPreserve.forEach(id => {
        const node = this.svgGraph[id]
        const saved = savedPositions.get(id)
        if (node && saved) {
          node.x = saved.x
          node.y = saved.y
        }
      })
    },
    delay (callback) {
      this.timeout = setTimeout(callback, 500)
    },
    resetDelay () {
      clearTimeout(this.timeout)
    },
    getCardBgClass (badge) {
      if (!badge?.color) {
        return ''
      }

      const rgb = `rgb(${badge.color.red},${badge.color.green},${badge.color.blue})`
      const name = getColorName(rgb)
      return name ? `theme-bg-${name}` : ''
    },
    getTextColour (cardType) {
      return cardTypesApi.getTextColour(cardType)
    },
    getBackgroundColour (cardType) {
      return cardTypesApi.getBackgroundColour(cardType)
    },
    connectionHasLabel (parentId, childId) {
      const xDistance = this.svgGraph[childId].x - this.svgGraph[parentId].x
      if (xDistance > 160) {
        if (this.svgGraph[parentId].type === 'bpmn:ExclusiveGateway') {
          return true
        }
      }
      return false
    },
    backwardConnectionHasLabel (parentId, childId) {
      const xDistance = this.svgGraph[childId].x - this.svgGraph[parentId].x
      if (xDistance <= 0) {
        if (this.svgGraph[parentId].type === 'bpmn:ExclusiveGateway') {
          return true
        }
      }
      return false
    },
    getBackwardLabelPosition (parentId, childId) {
      const coordinates = svgPathHelpers.getConnectionCoordinates(this.svgGraph[parentId], this.svgGraph[childId])
      const { x, y } = svgPathHelpers.getBackwardConnectionLabelPosition(coordinates)

      return { x: x - 80, y: y - 16 }
    },
    createPathBetweenNodes (parentId, childId) {
      const parentNode = this.svgGraph[parentId]
      const childNode = this.svgGraph[childId]
      const xDistance = childNode.x - parentNode.x
      const coordinates = svgPathHelpers.getConnectionCoordinates(parentNode, childNode)
      this.handleMarkOccupied(parentNode, childNode, coordinates)
      const breakingPoint = svgPathHelpers.getBreakingPoint(parentNode.type, childNode.type)
      const channelOffset = this.connectionChannelOffsets[connectionChannelKey(parentId, childId)] || 0
      if (xDistance > breakingPoint) {
        return svgPathHelpers.createForwardConnection(parentNode.type, coordinates, channelOffset)
      } else if (xDistance <= 0) {
        const childIsStartNode = childNode.parents.find(it => it === 'start')

        return svgPathHelpers.createBackwardConnection(childIsStartNode, coordinates, channelOffset)
      } else {
        return svgPathHelpers.createVerticalLine(parentId, childId, coordinates)
      }
    },
    handleMarkOccupied (parentNode, childNode, { xStart, yStart, xEnd, yEnd }) {
      if (!this.dragging && (yStart !== yEnd || xEnd < xStart)) {
        let xSlot
        if (parentNode.type === 'bpmn:ExclusiveGateway') {
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
        } else {
          xSlot = positionHelpers.getSlotXFromPoint(childNode.x) - 1
        }
        let ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
        if (xEnd < xStart) {
          ySlotStart = ySlotStart + 1
        }
        const ySlotEnd = positionHelpers.getSlotYFromPoint(childNode.y)
        if (this.$route.params.eventId) {
          if (childNode.id !== this.$route.params.eventId && parentNode.id !== this.$route.params.eventId) {
            this.markOccupied(xSlot, ySlotStart, ySlotEnd)
            if (xEnd < xStart) {
              xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
              ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
              this.markOccupied(xSlot, ySlotStart, ySlotEnd + 1)
            }
          }
        }
      }
    },
    async addRole () {
      const roleName = 'Lane ' + ((this.workflow.lanes?.length || 0) + 1)
      await this.addLane(
        this.svgGraph,
        this.svgLanes,
        this.svgGroups,
        this.workflow,
        roleName,
        true
      )
    },
    async addShapeAI (parentId = 'start') {
      this.$refs.generator.addShapeToDiagram(parentId)
    },
    async addCardAI (eventId, cardTypeId) {
      this.$refs.generator.addCardToDiagram(eventId, cardTypeId)
    },
    async addShape (shape = {}, options = {}) {
      this.creatingEvent = true
      try {
        // use nextTick to wait for blur events to complete first to update descriptions or labels
        await this.$nextTick()
        const selectedEventId = this.$route.params.eventId && this.svgGraph[this.$route.params.eventId]?.id
        const parentId = options.parentEventId || selectedEventId || 'start'
        if (parentId !== 'start' && this.workflow?.domainEvents?.findIndex(event => event.id === parentId) === -1) {
          // prevent double click on the same event, not necessary check but makes frontend nicer
          return
        }
        shape.parents = [parentId]
        const newEventId = await workflowApi.addEventToWorkflow(
          shape,
          options,
          this.svgLanes,
          this.svgGraph,
          this.svgGroups,
          this.workflow
        )
        await this.$router.push({ path: '/workflow/' + this.workflow.projectId + '/' + this.workflow.id + '/' + newEventId }).catch(() => {})
        scrollToEvent(newEventId)
      } finally {
        this.creatingEvent = false
      }
    },
    async addParallelEvent () {
      const selectedEventId = this.$route.params.eventId
      if (!selectedEventId || !this.svgGraph[selectedEventId]) return
      const shape = { type: 'bpmn:Task' }
      shape.parents = [selectedEventId]
      this.creatingEvent = true
      try {
        await this.$nextTick()
        const newEventId = await workflowApi.addEventToWorkflow(
          shape,
          { parentEventId: selectedEventId, parallel: true },
          this.svgLanes,
          this.svgGraph,
          this.svgGroups,
          this.workflow
        )
        await this.$router.push({ path: '/workflow/' + this.workflow.projectId + '/' + this.workflow.id + '/' + newEventId }).catch(() => {})
        scrollToEvent(newEventId)
      } finally {
        this.creatingEvent = false
      }
    },
    // Batch paste multiple shapes at once with single updateWorkflow call
    async addMultipleShapes (parentId, shapes = [], newIdTooldId = new Map(), options = {}) {
      if (!parentId || !shapes || shapes.length === 0) {
        return []
      }

      this.creatingEvent = true
      try {
        await this.$nextTick()

        // Clone svgGraph, svgLanes, svgGroups to work with
        const svgGraph = JSON.parse(JSON.stringify(this.svgGraph))
        const svgLanes = JSON.parse(JSON.stringify(this.svgLanes))
        const svgGroups = JSON.parse(JSON.stringify(this.svgGroups))

        // Handle 'start' case - no parent event exists in svgGraph
        const parentEvent = parentId !== 'start' ? svgGraph[parentId] : null
        const orphanEventId = parentEvent && parentEvent.childIds && parentEvent.childIds.length === 1 ? parentEvent.childIds[0] : null

        // Find adult shapes (no external children) and output shape
        const { adults, shapesOutput } = options

        // Collect orphan descendants and preserve their positions
        const orphanDescendantsOffsets = new Map()
        if (orphanEventId) {
          const visited = new Set() // Prevent infinite loops

          // Recursively collect all descendants with groupId filtering
          const collectDescendants = (nodeId) => {
            if (visited.has(nodeId)) return
            visited.add(nodeId)

            const node = svgGraph[nodeId]
            if (!node) return

            // Store original offset for this node
            orphanDescendantsOffsets.set(nodeId, {
              offset: node.offset,
              y: node.y
            })

            // Traverse children
            if (node.childIds && node.childIds.length > 0) {
              for (const childId of node.childIds) {
                const childNode = svgGraph[childId]
                // Only include descendants with compatible groupId
                if (childNode) {
                  collectDescendants(childId)
                }
              }
            }
          }

          collectDescendants(orphanEventId)
        }

        const newEventIds = []
        const selectedEventId = this.$route.params.eventId && svgGraph[this.$route.params.eventId]?.id
        // Process each shape and add to svgGraph
        for (let i = 0; i < shapes.length; i++) {
          const { shape, shapeOptions } = shapes[i]
          shape.parents = shape.parents.map(parentId =>
            parentId === 'start' && selectedEventId ? selectedEventId : parentId
          )

          const sanitizedShape = workflowApi.sanitizeShapeForCopy(shape, shapeOptions, svgGraph, this.workflow)
          const newEventId = workflowApiHelpers.addNewShapeToSvgGraphAndAlignAllNodes(
            sanitizedShape,
            shapeOptions,
            svgLanes,
            svgGraph,
            svgGroups
          )
          newEventIds.push(newEventId)
        }

        // Handle output connections to parent event's child
        if (orphanEventId) {
          // Remove old connections to orphan event
          svgGraph[orphanEventId].parents.forEach(parentId => {
            const parentNode = svgGraph[parentId]
            if (parentNode && parentNode.childIds) {
              const index = parentNode.childIds.indexOf(orphanEventId)
              if (index !== -1) {
                parentNode.childIds.splice(index, 1)
              }
            }
          })

          // Update the connections for the orphan event to the actual new parent(s)
          svgGraph[orphanEventId].parents = shapesOutput ? [shapesOutput] : adults

          // Add orphan to the childIds of its new parent(s)
          for (const parentId of svgGraph[orphanEventId].parents) {
            const parentNode = svgGraph[parentId]
            if (parentNode) {
              if (!parentNode.childIds) {
                parentNode.childIds = []
              }
              if (!parentNode.childIds.includes(orphanEventId)) {
                parentNode.childIds.push(orphanEventId)
              }
            }
          }

          // Restore orphan descendants' vertical positions
          // This prevents them from being pushed down during shape insertion
          if (orphanDescendantsOffsets.size > 0) {
            for (const [nodeId, originalPos] of orphanDescendantsOffsets.entries()) {
              const node = svgGraph[nodeId]
              if (node && node.offset !== originalPos.offset) {
                // Only restore if the offset changed (was pushed down)
                node.offset = originalPos.offset
                node.y = originalPos.y
              }
            }
          }
        }

        // Single updateWorkflow call for all shapes
        if (newEventIds.length > 0) {
          // Count events and decisions, and collect their names
          let eventCount = 0
          let decisionCount = 0
          const names = []

          for (const eventId of newEventIds) {
            const node = svgGraph[eventId]
            if (node) {
              names.push(node.name || node.description)
              if (node.type === 'bpmn:ExclusiveGateway') {
                decisionCount++
              } else {
                eventCount++
              }
            }
          }

          // Build action description
          const parts = []
          if (eventCount > 0) {
            parts.push(`${eventCount} event${eventCount > 1 ? 's' : ''}`)
          }
          if (decisionCount > 0) {
            parts.push(`${decisionCount} decision${decisionCount > 1 ? 's' : ''}`)
          }
          const action = `pasted ${parts.join(' and ')}`

          await workflowApi.updateWorkflow({
            svgGraph,
            svgLanes,
            svgGroups,
            schemas: options?.schemas || undefined,
            workflow: this.workflow,
            updateNote: {
              action,
              target: names.join(', ')
            }
          })

          // Navigate to last pasted event
          const lastEventId = newEventIds[newEventIds.length - 1]
          await this.$router.push({
            path: '/workflow/' + this.workflow.projectId + '/' + this.workflow.id + '/' + lastEventId
          }).catch(() => {})
          scrollToEvent(lastEventId)

          this.clearMultiSelection()
        }

        return newEventIds
      } finally {
        this.creatingEvent = false
      }
    },
    // ----- Add-group placement mode: a grey boundary line tracks the cursor and is
    // dropped on click/release (press-drag-release), snapping to the column grid. -----
    startPlaceGroup () {
      if (this.$store.state.isWorkflowDisabled) {
        return
      }
      // Toggle off if the button is pressed again while already placing.
      if (this.placingGroup) {
        this.cancelPlaceGroup()
        return
      }
      this.placingGroup = true
      this.placeGroupSlotX = null
    },
    onPlaceGroupMove (event) {
      const { x } = this.clientToSvgCoords(event)
      // Snap to the column boundary under the cursor. Slot 0 is the fixed left
      // edge (first group), so the smallest droppable divider is slot 1.
      this.placeGroupSlotX = Math.max(1, positionHelpers.getSlotXFromPoint(x))
    },
    cancelPlaceGroup () {
      this.placingGroup = false
      this.placeGroupSlotX = null
      this.placeGroupPointerDown = false
    },
    // Escape cancels either an active group placement or an in-progress divider
    // move. Registered once (in mounted) so it also fires mid divider-drag, not
    // only during placement.
    onGroupEscapeKeydown (event) {
      if (event.key !== 'Escape') {
        return
      }
      if (this.placingGroup) {
        this.cancelPlaceGroup()
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (this.groupReorder?.active) {
        this.cancelGroupReorderDrag()
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (this.dragging && this.selectedElement && this.svgGroups[this.selectedElement.id]) {
        this.cancelGroupDividerDrag()
        event.preventDefault()
        event.stopPropagation()
      }
    },
    cancelGroupReorderDrag () {
      const reorder = this.groupReorder
      document.removeEventListener('mousemove', this.onGroupReorderMove)
      document.removeEventListener('mouseup', this.onGroupReorderEnd)
      document.body.style.userSelect = ''
      document.body.classList.remove('group-reordering')
      // Restore the pre-drag snapshot so the release commits nothing.
      if (reorder?.baseGroups) {
        this.svgGroups = safeStructuredClone(reorder.baseGroups)
        this.svgGraph = safeStructuredClone(reorder.baseGraph)
      }
      this.groupReorder = {
        active: false,
        groupId: null,
        fromIndex: -1,
        dropIndex: -1,
        ghostLeft: 0,
        ghostWidth: 0,
        draggedWidth: 0,
        firstGroupExtra: 0,
        draggedName: ''
      }
    },
    cancelGroupDividerDrag () {
      const snapshot = this.groupDragSnapshot
      if (snapshot) {
        for (const id in snapshot.groups) {
          if (this.svgGroups[id]) {
            this.svgGroups[id].startSlotX = snapshot.groups[id]
            this.svgGroups[id].x = positionHelpers.getGroupXPointFromSlot(snapshot.groups[id])
          }
        }
        for (const id in snapshot.events) {
          if (this.svgGraph[id]) {
            this.svgGraph[id].x = snapshot.events[id]
          }
        }
        this.recalculateGroupWidths()
      }
      // Drop the drag so the mouse release (endDrag) commits nothing.
      this.selectedElement = null
      this.dragging = false
      this.slotX = 0
      this.groupDragSnapshot = null
      this.triggerUpdateFromDrag = false
      this.disableAutoscroll()
      this.enableNormalScroll()
    },
    commitPlaceGroup () {
      const slotX = this.placeGroupSlotX
      if (slotX === null) {
        this.cancelPlaceGroup()
        return
      }
      // Ignore a drop on an existing divider — nothing to split there.
      if (Object.values(this.svgGroups).some((group) => group.startSlotX === slotX)) {
        this.cancelPlaceGroup()
        return
      }

      // An ungrouped diagram is one implicit group. Materialise it at slot 0 so
      // the new divider has a group on both sides.
      if (!this.hasGroups) {
        const firstGroup = {
          id: randomUUID(),
          name: 'Group 1',
          startSlotX: 0,
          x: positionHelpers.getGroupXPointFromSlot(0),
          width: -1
        }
        this.svgGroups[firstGroup.id] = firstGroup
      }

      const group = {
        id: randomUUID(),
        name: 'Group ' + (Object.values(this.svgGroups).length + 1),
        startSlotX: slotX,
        x: positionHelpers.getGroupXPointFromSlot(slotX),
        width: -1
      }
      this.svgGroups[group.id] = group

      // Recompute widths and reassign events to whichever side of the divider they fall on.
      this.recalculateGroupWidths()
      workflowApiHelpers.updateGroupStartingPosition(this.svgGraph, this.svgGroups)

      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'added group',
          target: group.name
        }
      })

      this.cancelPlaceGroup()
    },
    addGroup (anchorEventId) {
      let startSlotX = 0
      const groupArray = Object.values(this.svgGroups)
      if (this.hasGroups) {
        const columnCount = this.baseData.columns.length
        const lengthArray = groupArray.map(group => group.startSlotX)
        const lastGroup = Math.max(...lengthArray)
        startSlotX = Math.max(lastGroup + 1, columnCount)
      }

      const group = {
        id: randomUUID(),
        name: 'Group ' + (groupArray.length + 1),
        startSlotX,
        x: positionHelpers.getGroupXPointFromSlot(startSlotX),
        width: -1
      }
      this.svgGroups[group.id] = group

      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'added group',
          target: group.name
        }
      })
    },
    addIntermediateGroup (currentGroup, direction) {
      const SLOT_WIDTH = 160 // One slot is 160 pixels wide
      const groupArray = Object.values(this.svgGroups)
      const sortedGroups = this.sortedGroups
      let currentIndex = sortedGroups.findIndex(g => g.id === currentGroup.id)
      currentIndex = direction === 'after' ? currentIndex + 1 : currentIndex

      // If currentIndex is the last one (or beyond), call addGroup instead
      if (currentIndex >= sortedGroups.length) {
        return this.addGroup()
      }

      const newGroupPositions = []
      let startX = positionHelpers.getXPointFromSlot(sortedGroups[0].startSlotX)
      for (let i = 0; i < sortedGroups.length; i++) {
        const group = sortedGroups[i]
        const index = group.index >= currentIndex ? group.index + 1 : group.index
        newGroupPositions.push({ id: group.id, startX, index })
        startX += group.width
      }

      const newStartSlotX = sortedGroups[currentIndex].startSlotX
      const newGroup = {
        id: randomUUID(),
        name: 'Group ' + (groupArray.length + 1),
        startSlotX: newStartSlotX,
        x: positionHelpers.getGroupXPointFromSlot(newStartSlotX),
        width: 160,
        index: currentIndex
      }
      this.svgGroups[newGroup.id] = newGroup

      // Move all events that are at or beyond the insertion point
      // Events in groups should be aligned with their group's new position
      // Events not in groups should be moved by one slot
      const insertionX = positionHelpers.getGroupXPointFromSlot(newStartSlotX)
      for (const eventId in this.svgGraph) {
        const event = this.svgGraph[eventId]

        if (event.x + 26 >= insertionX) {
          const newGroup = newGroupPositions.find(g => event.x + 26 === g.startX || event.x === g.startX)
          if (newGroup && !event.groupId && newGroup.index > 0) {
            event.groupId = newGroup.id
          }
          event.x += SLOT_WIDTH
        }
      }

      // Shift all groups from currentIndex onwards by +1 slot
      const groupsToShift = sortedGroups.slice(currentIndex)
      for (const group of groupsToShift) {
        this.svgGroups[group.id].startSlotX += 1
        this.svgGroups[group.id].index += 1
        this.svgGroups[group.id].x = positionHelpers.getGroupXPointFromSlot(this.svgGroups[group.id].startSlotX)
      }

      this.recalculateGroupWidths()

      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: `added group ${direction}`,
          target: currentGroup.name
        }
      })
    },
    recalculateGroupWidths () {
      // Sort groups by startSlotX
      const sortedGroups = Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)

      // Recalculate widths based on sorted order
      for (let i = 0; i < sortedGroups.length; i++) {
        const group = sortedGroups[i]
        const groupInSvgGroups = this.svgGroups[group.id]

        if (i === sortedGroups.length - 1) {
          // Last group has width -1
          groupInSvgGroups.width = -1
        } else {
          // Calculate width based on next group's position
          const nextGroup = sortedGroups[i + 1]
          groupInSvgGroups.width = positionHelpers.getGroupXPointFromSlot(nextGroup.startSlotX) - groupInSvgGroups.x
        }
      }
    },
    // ----- Lane drag-reorder (live reflow) -----
    startLaneReorder (laneId, index, event) {
      if (this.$store.state.isWorkflowDisabled) {
        return
      }
      const baseLanes = safeStructuredClone(this.svgLanes)
      const baseOrder = Object.values(this.svgLanes).sort((a, b) => a.index - b.index).map((lane) => lane.id)
      const draggedLane = baseLanes[laneId]
      const svgEl = this.$refs.innerSvg || document.getElementById('svg')
      const rectTop = svgEl ? svgEl.getBoundingClientRect().top : 0

      // Snapshot at drag start so thresholds stay fixed while the diagram reflows underneath.
      this.laneReorder = {
        active: true,
        laneId,
        dropIndex: index,
        grabOffset: (event.clientY - rectTop) - draggedLane.y,
        draggedHeight: draggedLane.height,
        draggedName: draggedLane.name,
        ghostTop: draggedLane.y,
        // Other lanes: midpoint + whether each starts above the dragged lane.
        others: baseOrder
          .map((id, i) => ({ id, i }))
          .filter((entry) => entry.id !== laneId)
          .map((entry) => ({
            id: entry.id,
            mid: baseLanes[entry.id].y + baseLanes[entry.id].height / 2,
            above: entry.i < index
          })),
        baseLanes,
        baseGraph: safeStructuredClone(this.svgGraph),
        baseOrder
      }
      document.body.style.userSelect = 'none'
      document.body.classList.add('lane-reordering')
      document.addEventListener('mousemove', this.onLaneReorderMove)
      document.addEventListener('mouseup', this.onLaneReorderEnd, { once: true })
    },
    onLaneReorderMove (event) {
      if (!this.laneReorder.active) {
        return
      }
      const svgEl = this.$refs.innerSvg || document.getElementById('svg')
      if (!svgEl) {
        return
      }
      const rect = svgEl.getBoundingClientRect()
      // Leading edge per direction: top edge vs lanes above, bottom edge vs lanes below.
      const cursorY = event.clientY - rect.top
      const topEdge = cursorY - this.laneReorder.grabOffset
      const bottomEdge = topEdge + this.laneReorder.draggedHeight
      this.laneReorder.ghostTop = Math.max(0, topEdge)
      let insertionIndex = 0
      for (const other of this.laneReorder.others) {
        if (other.above) {
          if (topEdge >= other.mid) {
            insertionIndex++
          }
        } else if (bottomEdge > other.mid) {
          insertionIndex++
        }
      }
      if (insertionIndex !== this.laneReorder.dropIndex) {
        this.laneReorder.dropIndex = insertionIndex
        this.previewLaneReorder()
      }
    },
    // Live (frontend-only) preview: insert the dragged lane into the other lanes at the current
    // target index and recompute the layout from the drag-start snapshot.
    previewLaneReorder () {
      const { laneId, dropIndex, others, baseLanes, baseGraph } = this.laneReorder
      const newOrder = others.map((other) => other.id)
      newOrder.splice(dropIndex, 0, laneId)

      const { svgLanes, svgGraph } = recomputeLaneLayout(baseLanes, baseGraph, newOrder)
      this.svgLanes = svgLanes
      this.svgGraph = svgGraph
    },
    onLaneReorderEnd () {
      document.removeEventListener('mousemove', this.onLaneReorderMove)
      document.body.style.userSelect = ''
      document.body.classList.remove('lane-reordering')
      const reorder = this.laneReorder
      this.laneReorder = { active: false, laneId: null, dropIndex: -1 }
      if (!reorder.active) {
        return
      }

      const finalOrder = Object.values(this.svgLanes)
        .sort((a, b) => a.index - b.index)
        .map((lane) => lane.id)
      const changed = finalOrder.some((id, index) => id !== reorder.baseOrder[index])

      const order = changed ? finalOrder : reorder.baseOrder
      const { svgLanes, svgGraph } = recomputeLaneLayout(reorder.baseLanes, reorder.baseGraph, order)
      this.svgLanes = svgLanes
      this.svgGraph = svgGraph

      if (!changed) {
        return
      }

      // Save the final order to the backend.
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'reordered lanes',
          target: reorder.baseLanes[reorder.laneId]?.name
        }
      })
    },
    updateGroupName (event, group) {
      const { textContent: newValue } = event.target

      if (newValue === this.svgGroups[group.id].name) {
        return
      }

      this.svgGroups[group.id].name = newValue
      if (event.relatedTarget?.classList?.contains('captures-description')) {
        // another button was pushed that will
        // handle the update of the group name
        return
      }
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'changed name of group',
          target: group.name
        }
      }, true)
    },
    // No confirmation on either action — both are undo-able.
    ungroup (group) {
      workflowApi.deleteGroupKeepEvents(group, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    deleteGroup (group) {
      workflowApi.deleteGroup(group, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    async deleteLane (id, index) {
      const name = this.svgLanes[id]?.name
      workflowApiHelpers.deleteLane(id, this.svgLanes, this.svgGraph)

      await workflowApi.updateWorkflow({
        svgLanes: this.svgLanes,
        svgGraph: this.svgGraph,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'deleted lane',
          target: name
        }
      })
    },
    isLaneEmpty (laneId) {
      if (!this.svgGraph) {
        return true
      }
      return !Object.values(this.svgGraph).some((node) => node.laneId === laneId)
    },
    enableAutoscroll (event) {
      this.clientY = event.clientY // save starting pos
      this.autoscroll = setInterval(() => {
        if (this.dragging) {
          if (this.clientY < 93 && this.selectedElement.y > 0) {
            // autoscroll up
            window.scrollBy(0, -5)
            this.scrollTop -= 5
            this.selectedElement.y -= 5
          } else {
            // autoscroll down
            const boundingRect = document.getElementById('flowchart')?.getBoundingClientRect()
            if (boundingRect) {
              const bottomOfSvg = boundingRect.y + boundingRect.height
              if ((window.innerHeight - this.clientY) < 93 && (bottomOfSvg - this.clientY) > 30) {
                window.scrollBy(0, 5)
                this.scrollTop += 5
                this.selectedElement.y += 5
              }
            }
          }
        }
      }, 20)
    },
    disableAutoscroll () {
      clearInterval(this.autoscroll)
    },
    updateAutoscrollParameters (event) {
      this.clientY = event.clientY
    },
    startDrag (event) {
      if (this.placingGroup) {
        // Press-drag-release: mousedown only blocks the normal canvas behaviour
        // (selection/pan) and marks that the press began on the canvas; the
        // divider is dropped on mouseup (endDrag).
        this.placeGroupPointerDown = true
        this.onPlaceGroupMove(event)
        event.preventDefault()
        return
      }
      const isRect = event.target?.nodeName?.toLowerCase() === 'rect'
      const isDraggableRect = isRect && event.target?.classList?.contains('draggable') && !event.target?.classList?.contains('horizontal')
      if (event?.button === 0 && isDraggableRect) {
        return
      }
      // Embed: dragging anywhere on the board pans; a plain click (no movement) still
      // falls through to open the event details.
      if (this.isEmbedMode && event?.button === 0) {
        this.isDraggingCanvas = true
        this.draggingCanvasStartX = event.clientX
        this.draggingCanvasStartY = event.clientY
        this.embedPanMoved = false
        return
      }
      const isLaneRect = event.target?.classList?.contains('lane-rect')
      if (event?.button === 0 && isLaneRect) {
        // Check interaction mode
        if (this.$store.state.interactionMode === 'pointer') {
          this.startSelection(event)
          return
        }
        if (this.$store.state.interactionMode === 'hand') {
          this.isDraggingCanvas = true
          this.draggingCanvasStartX = event.clientX
          this.draggingCanvasStartY = event.clientY
          return
        }
      }
      if (!this.selectedElement && !this.$store.state.isWorkflowDisabled) {
        const targetId = event.target.id.replace('p-', '')
        const isDraggable = event.target.classList.contains('draggable')
        if (isDraggable) {
          if (this.svgGraph[targetId]) {
            this.selectedElement = this.svgGraph[targetId]
            document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
              el.removeAttribute('data-test-slot')
            })
          }

          if (this.svgGroups[targetId]) {
            this.selectedElement = this.svgGroups[targetId]
          }

          if (this.selectedElement) {
            // Clear multi-selection if dragging a single element that's not part of the multi-selection
            if (this.selectedEventIds?.length > 1 && !this.selectedEventIds.includes(this.selectedElement.id)) {
              this.clearMultiSelection()
            }

            this.dragging = true
            this.dragOffsetX = event.pageX - this.selectedElement.x
            this.dragOffsetY = event.pageY - this.selectedElement.y
            this.startDragX = event.pageX
            this.startDragY = event.pageY
            this.watchInPause = true
            this.slotX = 0
            if (this.svgGraph[this.selectedElement.id]) {
              this.slotX = positionHelpers.getSlotXFromId(this.selectedElement.id, this.svgGraph)
            } else if (this.svgGroups[this.selectedElement.id]) {
              this.slotX = this.svgGroups[this.selectedElement.id].startSlotX
            }
            this.triggerUpdateFromDrag = false
            this.currentScrollLeft = this.$refs.scrollContainer.scrollLeft
            this.disableNormalScroll()
            this.enableAutoscroll(event)
          }
        }
      }
    },
    endDrag (update = true) {
      if (this.placingGroup) {
        // endDrag is bound to document, not just the svg, so only a press that
        // began on the canvas may drop the boundary. A mouseup anywhere else
        // (a tab, a panel, the Add group button) must not commit.
        if (this.placeGroupPointerDown) {
          this.commitPlaceGroup()
        }
        this.placeGroupPointerDown = false
        return
      }
      if (this.isSelectionDragging) {
        this.stopSelectionDrag(update)
        return
      }
      // Prevent handling endDrag immediately after selection drag finishes
      if (this.justFinishedDragging) {
        this.justFinishedDragging = false
        return
      }
      this.disableAutoscroll()
      this.enableNormalScroll()
      if (this.isSelecting || this.selectionStart) {
        this.finishSelection()
      }
      if (this.selectedElement) {
        if (this.svgGraph[this.selectedElement.id]) {
          // dragging a shape
          this.snapToGrid(this.selectedElement)
          const yMovement = Math.abs(this.dragOffsetY - this.startDragY + this.selectedElement.y)
          document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
            el.removeAttribute('data-test-slot')
          })

          const svgGraphClone = safeStructuredClone(this.svgGraph)

          if (this.showReleaseNo) {
            // In case if we have applied filter by release number (showReleaseNo), the cards in the
            // svgGraphData will be different from original value and will trigger unnecessary update, so we need
            // to set it undefined to prevent that

            Object.keys(svgGraphClone).forEach((key) => {
              svgGraphClone[key].cards = undefined
            })
          }

          if (update && (this.triggerUpdateFromDrag || yMovement > 0)) {
            workflowApi.updateWorkflow({
              svgGraph: svgGraphClone,
              svgLanes: this.svgLanes,
              svgGroups: this.svgGroups,
              workflow: this.workflow,
              updateNote: {
                action: this.selectedElement.type === 'bpmn:ExclusiveGateway' ? 'moved decision' : 'moved event',
                target: this.selectedElement.name
              }
            })
          }
        }

        if (this.svgGroups[this.selectedElement.id]) {
          // dragging a group divider
          this.svgGroups[this.selectedElement.id].startSlotX = this.slotX
          this.selectedElement.x = positionHelpers.getGroupXPointFromSlot(this.slotX)
          workflowApi.updateGroupStartingPosition(this.selectedElement.id, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
        }
      }
      this.selectedElement = null
      this.dragging = false
      this.dragOffsetX = null
      this.dragOffsetY = null
      this.lastComittedSlotX = null
      this.groupDragSnapshot = null
      this.triggerUpdateFromDrag = false
      this.currentScrollLeft = 0
      this.isDraggingCanvas = false
      this.draggingCanvasStartX = 0
      this.draggingCanvasStartY = 0
      // If the embed drag actually panned, swallow the click that follows so it doesn't
      // open an event's details after a pan.
      if (this.embedPanMoved) {
        this.embedPanMoved = false
        const suppressClick = (e) => {
          e.stopPropagation()
          e.preventDefault()
        }
        // Fire at most once; also clear it next tick in case no click follows the drag,
        // so it can't suppress an unrelated click later.
        window.addEventListener('click', suppressClick, { capture: true, once: true })
        setTimeout(() => window.removeEventListener('click', suppressClick, true), 0)
      }
    },
    drag (event) {
      if (this.placingGroup) {
        this.onPlaceGroupMove(event)
        return
      }
      if (this.isSelecting) {
        this.updateSelection(event)
        return
      }
      this.clientY = event.offsetY
      this.screenY = event.screenY
      if (this.selectedElement) {
        this.updateAutoscrollParameters(event)
        this.watchInPause = false
        if (this.svgGraph[this.selectedElement.id]) {
          // Dragging an event
          this.dragging = true
          this.selectedElement.x = event.pageX - this.dragOffsetX + (this.$refs.scrollContainer.scrollLeft - this.currentScrollLeft)
          this.selectedElement.y = event.pageY - this.dragOffsetY
          const { slotX } = positionHelpers.getSlotsFromNode(this.selectedElement, this.svgGraph)
          if (
            (
              slotX === (this.slotX + 1) &&
              (
                this.hasSlotToTheRight(this.selectedElement) ||
                !this.selectedElement.childIds?.length > 0 ||
                this.hasGroups
              )
            ) ||
            (
              slotX === (this.slotX - 1) &&
              this.hasSlotToTheLeft(this.selectedElement)
            )
          ) {
            this.slotX = slotX
            this.triggerUpdateFromDrag = true // changed slot
          }
        } else if (this.svgGroups[this.selectedElement.id]) {
          // Dragging a group divider left collapses the previous group's trailing
          // empty columns, sliding the events and dividers on the right along with
          // it. Dragging right just resizes the dragged divider.
          const sortedGroups = Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
          const draggedIndex = sortedGroups.findIndex((group) => group.id === this.selectedElement.id)
          const draggedGroup = sortedGroups[draggedIndex]
          const previousGroup = sortedGroups[draggedIndex - 1]
          const nextGroup = sortedGroups[draggedIndex + 1]
          if (!previousGroup) {
            return
          }

          // Snapshot divider slots, event positions and the gap once per gesture
          // so the collapse is recomputed from a stable baseline every frame.
          if (!this.groupDragSnapshot) {
            this.groupDragSnapshot = { groups: {}, events: {} }
            sortedGroups.forEach((group) => {
              this.groupDragSnapshot.groups[group.id] = group.startSlotX
            })
            for (const id in this.svgGraph) {
              this.groupDragSnapshot.events[id] = this.svgGraph[id].x
            }
            // Empty gap = columns between the previous group's last event and the divider.
            let previousLastEventSlot = previousGroup.startSlotX - 1
            for (const id in this.groupDragSnapshot.events) {
              const eventSlot = positionHelpers.getSlotXFromPoint(this.groupDragSnapshot.events[id])
              if (eventSlot >= previousGroup.startSlotX && eventSlot < draggedGroup.startSlotX && eventSlot > previousLastEventSlot) {
                previousLastEventSlot = eventSlot
              }
            }
            this.groupDragSnapshot.gap = draggedGroup.startSlotX - (previousLastEventSlot + 1)
          }
          const originGroupSlot = this.groupDragSnapshot.groups
          const originEventX = this.groupDragSnapshot.events
          const draggedOriginSlot = originGroupSlot[draggedGroup.id]
          const gap = this.groupDragSnapshot.gap

          // Free to drag left to one slot past the previous group, right to the next divider.
          const minSlot = originGroupSlot[previousGroup.id] + 1
          const maxSlot = nextGroup ? originGroupSlot[nextGroup.id] - 1 : minSlot + 9999
          const pointX = event.pageX - this.dragOffsetX + (this.$refs.scrollContainer.scrollLeft - this.currentScrollLeft)
          this.selectedElement.x = Math.min(Math.max(positionHelpers.getGroupXPointFromSlot(minSlot), pointX), positionHelpers.getGroupXPointFromSlot(maxSlot))
          this.selectedElement.y = event.pageY - this.dragOffsetY

          const targetSlot = Math.min(Math.max(positionHelpers.getSlotXFromPoint(this.selectedElement.x), minSlot), maxSlot)
          if (targetSlot !== this.slotX) {
            this.slotX = targetSlot
            this.triggerUpdateFromDrag = true // changed slot

            // Columns collapsed (0 when dragging right or when there is no gap).
            const removed = Math.max(0, Math.min(draggedOriginSlot - targetSlot, gap))

            // Slide the dividers on the right left as a block.
            draggedGroup.startSlotX = targetSlot
            for (let i = draggedIndex + 1; i < sortedGroups.length; i++) {
              const follower = sortedGroups[i]
              follower.startSlotX = originGroupSlot[follower.id] - removed
              follower.x = positionHelpers.getGroupXPointFromSlot(follower.startSlotX)
            }

            // Slide every event at or right of the divider left along with them.
            for (const id in this.svgGraph) {
              const original = originEventX[id]
              if (original === undefined) {
                continue
              }
              this.svgGraph[id].x = positionHelpers.getSlotXFromPoint(original) >= draggedOriginSlot
                ? original - removed * 160
                : original
            }

            // Recompute widths from the snapped divider slots.
            sortedGroups.forEach((group, i) => {
              group.width = i === sortedGroups.length - 1
                ? -1
                : positionHelpers.getGroupXPointFromSlot(sortedGroups[i + 1].startSlotX) - positionHelpers.getGroupXPointFromSlot(group.startSlotX)
            })
          }
        }
      }
      if (this.isDraggingCanvas) {
        this.$refs.scrollContainer.scrollLeft += this.draggingCanvasStartX - event.clientX
        this.draggingCanvasStartX = event.clientX
        // Vertical pan in embed: the page (window) owns vertical scroll, not the
        // container, so drag-pan moves the window like Figma/Miro do.
        if (this.isEmbedMode) {
          window.scrollBy(0, this.draggingCanvasStartY - event.clientY)
          this.draggingCanvasStartY = event.clientY
          this.embedPanMoved = true
        }
      }
    },
    getYPointFromSlot (slot) {
      return 30 + 140 * slot
    },
    hasSlotToTheLeft (selectedElement) {
      const isStartingPoint = (selectedElement.parents || []).findIndex(id => id === 'start') >= 0
      if (!isStartingPoint &&
        selectedElement.parents.findIndex(parentId => positionHelpers.getSlotXFromId(parentId, this.svgGraph) <= this.slotX) >= 0) {
        return true
      } else if (selectedElement.groupId && this.svgGroups[selectedElement.groupId].slotX !== 0) {
        return true
      } else {
        return false
      }
    },
    hasSlotToTheRight (selectedElement) {
      if (selectedElement.childIds?.length > 0 &&
        selectedElement.childIds.findIndex(childId => positionHelpers.getSlotXFromId(childId, this.svgGraph) >= this.slotX) >= 0) {
        return true
      } else {
        return false
      }
    },
    snapToGrid (draggedNode) {
      // snap to grid x
      const isStartingPoint = (draggedNode.parents || []).findIndex(id => id === 'start') >= 0
      if (draggedNode.groupId && this.svgGroups[draggedNode.groupId]) {
        draggedNode.x = 80 + this.svgGroups[draggedNode.groupId].startSlotX * 160
      } else if (draggedNode.parents && draggedNode.parents[0] && !isStartingPoint) {
        draggedNode.x = this.svgGraph[draggedNode.parents[0]].x + 160
      } else if (isStartingPoint) {
        draggedNode.x = 80
      } else {
        if (draggedNode.childIds && draggedNode.childIds[0]) {
          draggedNode.x = this.svgGraph[draggedNode.childIds[0]].x - 160
        }
      }

      // snap to grid y using helper function
      workflowApiHelpers.calculateNodeLaneAndOffset(draggedNode, this.svgLanes)
      this.svgGraph[draggedNode.id] = draggedNode

      // Expand lane if node is outside boundaries
      if (!workflowApiHelpers.isNodeWithinLaneBoundaries(draggedNode, this.svgLanes)) {
        this.svgLanes[draggedNode.laneId].height = (draggedNode.offset + 1) * 156
        workflowApiHelpers.updateLane(this.svgLanes[draggedNode.laneId], this.svgGraph, this.svgLanes)
      }

      workflowApiHelpers.updateVerticalPositionsIfCollision(this.svgGraph, this.svgLanes, draggedNode.id, true)
    },
    snapMultiSelectionToGrid (selectedEventIds) {
      if (!Array.isArray(selectedEventIds) || selectedEventIds.length === 0) {
        return
      }

      const selectedSet = new Set(selectedEventIds)

      // Step 1: Find the anchor node (node with external parent connection)
      let anchorNode = null
      for (const id of selectedEventIds) {
        const node = this.svgGraph?.[id]
        if (!node) continue

        if (Array.isArray(node.parents) && node.parents.length > 0) {
          for (const parentId of node.parents) {
            if (parentId !== 'start' && !selectedSet.has(parentId)) {
              // Found a node with external parent - this is our anchor
              anchorNode = node
              break
            }
          }
          if (anchorNode) break
        }
      }

      // If no external parent found, use first selected node as anchor
      if (!anchorNode) {
        const firstId = selectedEventIds[0]
        anchorNode = this.svgGraph?.[firstId]
      }

      if (!anchorNode) {
        return
      }

      // Step 2-4: Calculate snap delta using helper function
      // Note: We need to pass a COPY of anchorNode to avoid modifying it twice
      const anchorNodeCopy = { ...anchorNode }
      const { snapDeltaX, snapDeltaY, snappedX, snappedY } = workflowApiHelpers.calculateSnapDeltaForAnchor(
        anchorNodeCopy,
        this.svgGraph,
        this.svgGroups,
        this.svgLanes
      )

      // Apply the snapped position to the actual anchor node
      anchorNode.x = snappedX
      anchorNode.y = snappedY
      anchorNode.laneId = anchorNodeCopy.laneId
      anchorNode.offset = anchorNodeCopy.offset
      this.svgGraph[anchorNode.id] = anchorNode

      // Expand lane if node is outside boundaries
      if (!workflowApiHelpers.isNodeWithinLaneBoundaries(anchorNode, this.svgLanes)) {
        this.svgLanes[anchorNode.laneId].height = (anchorNode.offset + 1) * 156
        workflowApiHelpers.updateLane(this.svgLanes[anchorNode.laneId], this.svgGraph, this.svgLanes)
      }

      // Step 5: Apply same delta to all other selected nodes (preserving relative positions)
      selectedEventIds.forEach(id => {
        if (id !== anchorNode.id) {
          const node = this.svgGraph?.[id]
          if (node) {
            // Move by same amount as anchor - PRESERVES INTERNAL CONNECTIONS
            node.x += snapDeltaX
            node.y += snapDeltaY

            // Update lane info based on new Y position using helper function
            workflowApiHelpers.calculateNodeLaneAndOffset(node, this.svgLanes)
            this.svgGraph[node.id] = node

            // Expand lane if node is outside boundaries
            if (!workflowApiHelpers.isNodeWithinLaneBoundaries(node, this.svgLanes)) {
              this.svgLanes[node.laneId].height = (node.offset + 1) * 156
              workflowApiHelpers.updateLane(this.svgLanes[node.laneId], this.svgGraph, this.svgLanes)
            }
          }
        }
      })

      // Step 6: Handle collisions with external (non-selected) shapes only
      // Check each selected node for collisions with non-selected nodes
      selectedEventIds.forEach(id => {
        const node = this.svgGraph?.[id]
        if (node) {
          // Check if this node collides with any non-selected node
          let targetNode = null
          for (const otherId in this.svgGraph) {
            if (!selectedSet.has(otherId)) {
              const otherNode = this.svgGraph[otherId]
              if (otherNode.placeholder !== true &&
                otherNode.x === node.x &&
                otherNode.y === node.y &&
                otherNode.laneId === node.laneId) {
                targetNode = otherNode
                break
              }
            }
          }
          // If collision found with external shape, move the external shape down
          if (targetNode) {
            workflowApiHelpers.updateVerticalPositionsIfCollision(
              this.svgGraph,
              this.svgLanes,
              targetNode.id, // Move the OTHER shape, not the selected one
              true
            )
          }
        }
      })
    },
    disableNormalScroll () {
      // Get the current page scroll position
      this.scrollTop = window.pageYOffset || document.documentElement.scrollTop
      this.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

      // if any scroll is attempted, set this to the previous value
      window.onscroll = () => {
        window.scrollTo(this.scrollLeft, this.scrollTop)
      }
    },
    enableNormalScroll () {
      // remove the eventhandler by assigning an empty function
      window.onscroll = function () { }
    },
    computeSwimlanePath (x, y, width, height) {
      y = y - 0.5
      return `M${x},${y}h${width}`
    },
    getActionsMenuXPosition () {
      const minX = 33
      if (this.selectionBoundingRect) {
        // Center the menu at the bottom of the bounding rect
        // Using transform: translateX(-50%) to center, so we position at the center X
        const rectCenterX = this.selectionBoundingRect.x + (this.selectionBoundingRect.width / 2)
        return Math.max(minX, rectCenterX)
      }
      const node = this.primarySelectedEvent
      if (!node) {
        return minX
      }
      const value = node.x - 117
      return Math.max(minX, value)
    },
    constrainActionsMenuYPosition () {
      let offset = 105
      if (this.hasGroups) {
        offset = 129
      }
      if (this.selectionBoundingRect) {
        // Position at the bottom of the bounding rect with some padding
        return this.selectionBoundingRect.y + this.selectionBoundingRect.height + 8
      }
      const node = this.primarySelectedEvent
      if (!node) {
        return offset
      }
      return node.y + offset
    },
    closeTransformOptions () {
      return { handler: this.hideTransformOptions, include: this.getBodyIfSubworkflowDialogIsOpen }
    },
    getBodyIfSubworkflowDialogIsOpen () {
      if (document.querySelector('.selectSubworkflowDialog')) {
        return [document.querySelector('body')]
      } else {
        return []
      }
    },
    hideTransformOptions () {
      this.showTransformOptions = false
    },
    handleScroll (ev) {
      const scrollLeft = this.$refs.scrollContainer.scrollLeft
      this.setBpmnScrollLeft(scrollLeft)
    },
    handleWindowResize () {
      const svgContainerWidth = window.innerWidth - 40
      this.setSvgContainerWidth(svgContainerWidth)
      this.measureVisibleBoardRight()
      // Re-fit on resize unless the viewer has taken manual control of the zoom.
      if (this.isEmbedMode && !this.embedUserZoomed) {
        this.embedFitToScreen()
      }
    },
    // Shrinks the board to fit the frame on load (never enlarges past 100%).
    embedFitToScreen () {
      const container = this.$refs.scrollContainer
      const availWidth = container?.clientWidth || window.innerWidth
      const availHeight = container?.clientHeight || window.innerHeight
      const width = this.diagramWidth
      const height = this.flowChartHeight
      if (!width || !height || !availWidth || !availHeight) {
        return
      }
      const fit = Math.min(availWidth / width, availHeight / height)
      this.embedZoom = Math.max(EMBED_ZOOM_MIN, Math.min(1, Math.round(fit * 100) / 100))
    },
    shouldSwitchWithChild (newSlotX, oldSlotX, { childNode }, preserveOrder) {
      return (
        childNode &&
        newSlotX > oldSlotX &&
        positionHelpers.getSlotXFromId(childNode.id, this.svgGraph) === newSlotX &&
        !preserveOrder
      )
    },
    shouldSwitchWithParent (newSlotX, oldSlotX, { parentNode }, preserveOrder) {
      return (
        parentNode &&
        newSlotX < oldSlotX &&
        newSlotX === positionHelpers.getSlotXFromId(parentNode.id, this.svgGraph) &&
        !preserveOrder
      )
    },
    // set the attribute of a DOM element
    // for testing purposes
    setDOMAttribute (id, attribute, value) {
      document.getElementById(id).setAttribute(attribute, value)
    },
    markOccupied (xSlot, ySlotStart, ySlotEnd) {
      const loopStart = Math.min(ySlotStart, ySlotEnd)
      const loopEnd = Math.max(ySlotStart, ySlotEnd)
      for (let y = loopStart; y <= loopEnd; y++) {
        const key = `${xSlot},${y}`
        this.occupiedSlotsVerticalLines[key] = true
      }
    },
    isColliding (xSlot, ySlotStart, ySlotEnd, backwardsConnection) {
      let occupiedCounter = 0
      const loopStart = Math.min(ySlotStart, ySlotEnd)
      const loopEnd = Math.max(ySlotStart, ySlotEnd)
      for (let y = loopStart; y <= loopEnd; y++) {
        const key = `${xSlot},${y}`
        if (this.occupiedSlotsVerticalLines?.[key]) {
          occupiedCounter++
          if (occupiedCounter > 1 || (backwardsConnection && occupiedCounter > 0)) {
            return true
          }
        }
      }
      return false
    }
  },
  mounted () {
    this.latestEventId = this.$route.params.eventId
    this.handleWindowResize()
    window.addEventListener('resize', this.handleWindowResize)
    document.addEventListener('mouseup', this.endDrag)
    // Capture phase is required: .diagram-container has `keydown.stop`, so a
    // bubble-phase listener here would never receive Escape from the diagram.
    document.addEventListener('keydown', this.onGroupEscapeKeydown, true)
    // Test seam (Cypress only): expose this component so E2E setup helpers can
    // append a group programmatically instead of driving the placement UI. No-op
    // in production because window.Cypress is undefined there.
    if (window.Cypress) {
      window.__qlerifyBpmn = this
    }
    this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll)
    const svg = document.getElementById('flowchart')
    if (svg) {
      svg.addEventListener('mousedown', this.startDrag)
      svg.addEventListener('mousemove', this.drag)
      svg.addEventListener('mouseup', this.endDrag)
    }
    EventBus.$on('enable-connection-targets', val => {
      this.enableConnectionTargets = val
      if (val) {
        if (!this.onEscPressed) {
          this.onEscPressed = event => {
            EventBus.$emit('enable-connection-targets', false)
          }
          window.addEventListener('keyup', this.onEscPressed)
        }
      } else {
        if (this.onEscPressed) {
          window.removeEventListener('keyup', this.onEscPressed)
          delete this.onEscPressed
        }
      }
    })
    EventBus.$on('reset-diagram', () => {
      this.resetDiagram()
    })
    EventBus.$on('open-card-comments', item => {
      if (item.eventId) {
        this.inspectEvent(item.eventId, 'comments')
      }
    })
    this.svgLanes = Object.assign({}, this.svgLanesData)
    this.resetDiagram()

    this.cardsByEventId = this.computeCardsByEventId()

    this.$nextTick(() => {
      Object.values(this.svgGroups).forEach(group =>
        this.updateFontSizeDynamically(this.$refs[`groupName-${group.id}`][0], true)
      )
    })
    this.updateShowActions()

    if (this.isEmbedMode) {
      this.$nextTick(() => this.embedFitToScreen())
      // On document so wheel-zoom works anywhere in the embed, including empty area.
      document.addEventListener('wheel', this.onEmbedWheel, { passive: false })
      // Show controls whenever the pointer is in the frame; hide when it leaves.
      document.addEventListener('mousemove', this.showEmbedControls)
      document.documentElement.addEventListener('mouseleave', this.hideEmbedControls)
    }
  },

  beforeUnmount () {
    EventBus.$off('enable-connection-targets')
    EventBus.$off('reset-diagram')
    EventBus.$off('open-card-comments')
    document.removeEventListener('mousemove', this.onLaneReorderMove)
    document.removeEventListener('mouseup', this.onLaneReorderEnd)
    document.body.classList.remove('lane-reordering')
    window.removeEventListener('resize', this.handleWindowResize)
    document.removeEventListener('mouseup', this.endDrag)
    document.removeEventListener('keydown', this.onGroupEscapeKeydown, true)
    if (window.Cypress && window.__qlerifyBpmn === this) {
      window.__qlerifyBpmn = null
    }
    this.cancelPlaceGroup()
    this.clearGroupDragCandidate()
    document.removeEventListener('mousemove', this.onGroupReorderMove)
    document.removeEventListener('mouseup', this.onGroupReorderEnd)
    document.body.classList.remove('group-reordering')
    this.$refs.scrollContainer?.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('wheel', this.onEmbedWheel)
    document.removeEventListener('mousemove', this.showEmbedControls)
    document.documentElement.removeEventListener('mouseleave', this.hideEmbedControls)
    const svg = document.getElementById('flowchart')
    if (svg) {
      svg.removeEventListener('mousedown', this.startDrag)
      svg.removeEventListener('mousemove', this.drag)
      svg.removeEventListener('mouseup', this.endDrag)
    }
    if (this.onEscPressed) {
      window.removeEventListener('keyup', this.onEscPressed)
    }
    document.removeEventListener('mousemove', this.handleSelectionDrag)
    document.removeEventListener('mousemove', this.updateConnectionDrag)
    document.removeEventListener('mouseup', this.endConnectionDrag)
    document.removeEventListener('mousemove', this.updateConnectionEdit)
    document.removeEventListener('mouseup', this.endConnectionEdit)
    document.removeEventListener('keydown', this.onConnectionKeydown, true)
    if (this.connectionDragJustFinishedTimeout) {
      clearTimeout(this.connectionDragJustFinishedTimeout)
      this.connectionDragJustFinishedTimeout = null
    }
    // Restore text selection in case the component unmounts mid-drag
    document.body.style.userSelect = ''
  },

  watch: {
    svgGraphData: {
      handler: function (newVal, oldVal) {
        this.occupiedSlotsVerticalLines = {}
        this.resetDiagram()
      },
      deep: true
    },
    svgLanesData: function (newLanesData, oldLanesData) {
      this.svgLanes = Object.assign({}, this.svgLanesData)
    },
    svgGroupsData: {
      handler: function (_, oldGroupsData) {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            Object.values(this.svgGroups).forEach(group => {
              const element = this.$refs[`groupName-${group.id}`]?.[0]
              if (element) {
                const oldGroup = oldGroupsData?.[group.id]
                if (!oldGroup || oldGroup.width !== group.width) {
                  this.updateFontSizeDynamically(element, true)
                }
              }
            })
          })
        })
      },
      deep: true
    },
    slotX: function (newSlotX, oldSlotX) {
      if (!this.watchInPause && this.svgGraph[this.selectedElement?.id]) {
        document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
          el.setAttribute('data-test-slot', newSlotX)
        })
        const involvedNodes = dragFunctions.getSurroundingNodes(this.selectedElement.id, this.svgGraph, this.svgGroups)
        let { switchedPlaces, preserveOrder } = dragFunctions.handleGroupSwitch(involvedNodes, newSlotX, oldSlotX, this.slotToGroupMap, this.svgGraph, this.svgGroups)
        if (this.shouldSwitchWithChild(newSlotX, oldSlotX, involvedNodes, preserveOrder)) {
          dragFunctions.switchCurrentNodeWithChild(involvedNodes, this.svgGraph, this.svgGroups) // dragging right
          switchedPlaces = true
        } else if (this.shouldSwitchWithParent(newSlotX, oldSlotX, involvedNodes, preserveOrder)) {
          dragFunctions.switchCurrentNodeWithParent(involvedNodes, this.svgGraph, this.svgGroups) // dragging left
          switchedPlaces = true
        }
        if (switchedPlaces) {
          this.selectedElement = workflowApiHelpers.refreshDiagramPartially(this.svgGraph, this.svgLanes, this.svgGroups, involvedNodes, this.selectedElement)
        }
      }
    },
    dragging (newVal) {
      if (newVal) {
        this.showActions = false
        return
      }
      this.updateShowActions()
    },
    disabled (newVal) {
      if (newVal) {
        const svg = document.getElementById('flowchart')
        svg.removeEventListener('mousedown', this.startDrag)
        svg.removeEventListener('mousemove', this.drag)
        svg.removeEventListener('mouseup', this.endDrag)
        // Clear any multi-selection when workflow becomes locked
        this.clearMultiSelection()
        // Switch to hand tool when workflow is locked
        this.$store.commit('setInteractionMode', 'hand')
      } else {
        const svg = document.getElementById('flowchart')
        svg.addEventListener('mousedown', this.startDrag)
        svg.addEventListener('mousemove', this.drag)
        svg.addEventListener('mouseup', this.endDrag)
      }
    },
    $route: {
      handler: function () {
        this.occupiedSlotsVerticalLines = {}
        this.showTransformOptions = false
        if (this.$route.params.eventId) {
          this.latestEventId = this.$route.params.eventId
          if (!this.hasClosedSidePanel) {
            // Only open until user has manually closed it once
            this.$store.commit('showSidePanel', true)
          }
          if (!this.selectionEvents.length) {
            this.selectedEventIds = [this.$route.params.eventId]
          }
          this.updateSelectionBoundingRect()
        } else {
          this.latestEventId = null
          if (!this.hasClosedSidePanel && ['cards', 'fields', 'fileUpload'].includes(this.sidePanelTab)) {
            this.$store.commit('showSidePanel', false)
          }
          if (!this.selectionEvents.length) {
            this.selectedEventIds = []
          }
          this.updateSelectionBoundingRect()
        }
        this.updateShowActions()
      },
      deep: true
    },
    bpmnScrollLeft (newVal) {
      // receive scroll event from the UserStoryMapping component
      this.$refs.scrollContainer.scrollLeft = newVal
    },
    '$store.state.interactionMode': function (newMode, oldMode) {
      // Clear selection when switching between hand and pointer tools
      if (newMode !== oldMode) {
        this.clearMultiSelection()
      }
    },
    selectedEventIds: {
      handler: function (newIds) {
        this.updateSelectionBoundingRect()
        this.updateShowActions()
        // Emit to parent for reactive prop binding
        this.$emit('update-selected-event-ids', newIds)
      },
      deep: true
    },
    events: {
      handler: function (newVal) {
        this.cardsByEventId = this.computeCardsByEventId()
      },
      deep: true
    }
  }
}

</script>

<style scoped>
svg {
  fill: #ffffff;
}

.ml-05 {
  margin-left: 2px;
}

.canvas-container {
  min-height: 140px;
  position: relative;
}

.scroll-container {
  position: relative;
  overflow-x: scroll;
  scrollbar-width: none;
  min-height: 140px;
}

.scroll-container.placing-group,
.scroll-container.placing-group * {
  cursor: col-resize !important;
}

.place-group-hint {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  display: flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 16px;
  background: rgba(46, 156, 230, 0.95);
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  white-space: nowrap;
}

.selection-box {
  position: absolute;
  border: 1px dashed #2e9ce6;
  background: rgba(46, 156, 230, 0.15);
  pointer-events: none;
  z-index: 10;
}

.selection-bounds {
  position: absolute;
  border: 1px solid #2e9ce6;
  background: rgba(46, 156, 230, 0.06);
  pointer-events: none;
  z-index: 11;
}

.selection-bounds-invalid {
  border: 1px dashed #e63946;
  background: rgba(230, 57, 70, 0.15);
  cursor: default;
  pointer-events: none;
}

.selection-error-message {
  position: fixed;
  background: var(--hint-color, #e63946);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  z-index: 99999;
}

.selection-error-message::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid var(--hint-color, #e63946);
}

.scrollable-container::-webkit-scrollbar {
  display: none;
}

.scroll-container::-webkit-scrollbar-thumb {
  height: 8px;
  border: 1px solid var(--background);
  border-radius: 5px;
  background-color: #aaa;
  background-origin: content-box;
}

.group {
  background: var(--diagram-group-header);
  height: 24px;
  padding: 4px 8px 3px 8px;
  text-align: center;
}

.group-first {
  border-top-left-radius: 4px;
}

.group-last {
  border-top-right-radius: 4px;
}

.group-header-draggable {
  cursor: grab;
}

.group-header-draggable:active {
  cursor: grabbing;
}

.group-header-editing {
  cursor: text;
}

.move-group-left-button,
.move-group-right-button,
.group-actions-button {
  width: 20px;
  position: absolute;
  top: 2px;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
  background: var(--light-2);
  border-radius: 20%;
  cursor: pointer;
}

.move-group-left-button {
  left: 2px;
}

.move-group-right-button {
  right: 24px;
}

.hover-container:hover .move-group-left-button,
.hover-container:hover .move-group-right-button,
.hover-container:hover .group-actions-button {
  opacity: 1;
}

.show-side-panel-button {
  position: fixed;
  top: 22px;
  right: -2px;
  z-index: 9;
}

.actions-enter-active,
.actions-leave-active {
  transition: opacity 0.15s ease;
}

.actions-enter-from,
.actions-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.2s ease-in-out;
}

.slide-in-enter-from {
  transform: translateX(370px);
}

.slide-in-enter-to {
  transform: translateX(0);
}

.slide-in-leave-from {
  transform: translateX(0);
}

.slide-in-leave-to {
  transform: translateX(370px);
}

.v-tooltip__content {
  background: var(--light-1);
  color: var(--text-dark);
  border-radius: 10px;
}

.v-tooltip__content code {
  background: var(--color-primary);
  color: var(--light-1);
}

.card-badge {
  cursor: pointer;
  width: 15px;
  height: 15px;
  text-align: center;
  line-height: 16px;
  border-radius: 2px !important;
  flex-shrink: 0;
}

.card-more-badge {
  cursor: pointer;
  text-align: center;
  border-radius: 2px !important;
  background-color: #363A40;
  color: #FCFCFC;
  flex-grow: 1;
  margin-left: 2px;
}

.card-badge-number {
  display: block;
  width: 100%;
  height: 100%;
  font-size: 12px;
}

.card-section-header {
  display: flex;
  justify-content: space-between;
}

.card-section-header > *:first-child {
  flex-grow: 1;
  margin-right: 4px;
}

.card-section-header > * {
  padding: 2px 8px;
  border-radius: 6px;
}

.card-description {
  border-bottom: 1px solid #e3e3e3;
}

.bpmn-event-container:focus-within + .subprocess-icon {
  display: none;
}

.subprocess-icon {
  border-radius: 4px;
  overflow: hidden;
}

.subprocess-icon .v-icon {
  outline: none !important;
}

.lane-title-container {
  width: 100%;
  height: 100%;
}

.lane-cell {
  fill: transparent;
  stroke: var(--diagram-lane-divider);
  stroke-width: 1px;
}

.lane-cell-menu-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.lane-cell-group:hover .lane-cell-menu-wrapper {
  opacity: 1;
}

.embed-zoom-controls {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px;
  border-radius: 6px;
  border: 1px solid var(--light-4);
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

.embed-attribution {
  position: fixed;
  left: 16px;
  bottom: 16px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--light-4);
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-dark);
  text-decoration: none;
}

.embed-attribution:hover {
  background: var(--light-2);
}

.embed-attribution-logo {
  width: 18px;
  height: 18px;
}

/* Fade the embed controls in only while the pointer is in the frame, to keep it clean. */
.embed-zoom-controls,
.embed-attribution {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.embed-zoom-controls.embed-controls-visible,
.embed-attribution.embed-controls-visible {
  opacity: 1;
  pointer-events: auto;
}

.embed-group-headers {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;
  overflow: hidden;
  background: var(--diagram-group-header);
  pointer-events: none;
}

.embed-group-header {
  position: absolute;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: var(--text-dark);
  border-right: 1px solid var(--diagram-lane-divider);
  box-sizing: border-box;
}

.lane-cell-menu-button {
  pointer-events: auto;
  width: 20px;
  height: 20px;
  border: none;
  background: var(--surface-elevated);
  border-radius: 4px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.lane-cell-menu-button:disabled {
  cursor: default;
  opacity: 0.6;
}

.lane-cell-menu-button:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 1px;
}

.v-avatar {
  border-radius: 30% !important;
  position: static;
}

.btn-add-event-to {
  border-radius: 50% !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 0px !important;
  border-radius: 8px !important;
}

.v-list-item {
  padding: 8px !important;
}

.v-list-item-title {
  font-size: 14px !important;
}
</style>

<style>
[contenteditable] {
  -webkit-user-select: text;
  user-select: text;
}

/* While dragging a lane or group, force the grabbing cursor everywhere so it doesn't flip to each
   element's own cursor (events, etc.) as the pointer passes over them. */
body.lane-reordering,
body.lane-reordering *,
body.group-reordering,
body.group-reordering * {
  cursor: grabbing !important;
}

.diagram-container {
  margin-right: -40px;
  margin-bottom: 0px;
  overflow: visible;
  min-height: 176px;
}

[data-theme="dark"] .diagram-container {
  background-color: var(--light-1);
}

.sequence-arrow {
  fill: none;
  stroke: var(--diagram-stroke);
  stroke-linejoin: round;
  stroke-linecap: round;
  marker-end: url('#sequenceflow-end-white-black-1r79xjtcycpwpzz6dais2xasb');
  pointer-events: none;
}

#flowchart .sequence-arrow.backward {
  stroke-dasharray: 6 4;
}

/* Stays at 2px: 3px would exactly close MIN_TRACK_SPACING, merging the crowded channels this is
   meant to help. */
#flowchart .sequence-arrow.highlighted {
  filter: none;
  stroke-width: 2px;
  marker-end: url('#connection-highlighted-arrow-end');
}

/* Emphasis by pushing the rest back, not by making the selection louder - the stroke is already near
   the top of its contrast range in both themes, and widening it would cost track spacing. At 0.5 the
   faded lines still clear the 3:1 non-text threshold, so context stays readable. */
#flowchart .sequence-arrow.dimmed {
  opacity: 0.5;
}

#flowchart .connection-hit-area {
  fill: none !important;
  stroke: transparent !important;
  stroke-width: 16px !important;
  pointer-events: stroke !important;
  cursor: pointer;
}

[data-connection-dragging="true"] #flowchart .connection-hit-area {
  pointer-events: none;
}

/* Pointing at a faded connection brings it back - you should always be able to inspect what you
   are pointing at, whatever is selected. */
#flowchart .connection-hit-area:hover + .sequence-arrow {
  stroke: rgb(46, 156, 230);
  stroke-width: 2px;
  opacity: 1;
  marker-end: url('#connection-hover-arrow-end');
}

#flowchart .sequence-arrow.editing-hidden {
  opacity: 0;
}

#flowchart .connection-ghost-line {
  stroke: rgb(46, 156, 230);
  stroke-width: 2px;
  stroke-dasharray: 6 4;
  fill: none;
  pointer-events: none;
}

#flowchart .connection-ghost-line.over-target {
  stroke-dasharray: none;
  marker-end: url('#connection-ghost-arrow-end');
}

.card-badge {
  cursor: pointer;
  width: 15px;
  height: 15px;
  text-align: center;
  line-height: 16px;
  border-radius: 2px !important;
  flex-shrink: 0;
}

.card-more-badge {
  cursor: pointer;
  text-align: center;
  border-radius: 2px !important;
  background-color: #363A40;
  color: #FCFCFC;
  flex-grow: 1;
  margin-left: 2px;
}

.card-badge-number {
  display: block;
  width: 100%;
  height: 100%;
  font-size: 12px;
}

.outlined {
  stroke-width: 0.5px !important;
}

path.highlighted {
  filter: drop-shadow(1px 1px 0px black);
}

[data-theme="dark"] path.highlighted {
  filter: drop-shadow(1px 1px 0px var(--diagram-stroke));
}

#flowchart path {
  stroke: var(--diagram-stroke);
  stroke-width: 1px;
  fill-opacity: 1;
}

#flowchart {
  display: block;
}

.swim-lane {
  stroke: var(--diagram-stroke);
  stroke-width: 2px;
  fill-opacity: 0;
}

/* .swim-lane:hover ~ .move-lane-down-button{
  display: block;
} */

.separator {
  height: 60px;
}

.create-first-event-button {
  padding-left: 10px;
  padding-right: 10px;
  text-transform: none !important;
}

.draggable {
  cursor: move !important;
}

.draggable.horizontal {
  cursor: ew-resize !important;
  user-select: none;
}

.notDraggable {
  cursor: default !important;
}

rect:not(.lane-rect):hover,
text:hover {
  cursor: pointer;
}

.lane-rect {
  cursor: grab;
}

.lane-rect:active {
  cursor: grabbing;
}

[data-interaction-mode="pointer"] .lane-rect {
  cursor: crosshair;
}

[data-interaction-mode="pointer"] .lane-rect:active {
  cursor: crosshair;
}

.diagram-actions {
  width: 100%;
  height: 36px;
  border-top: 1px solid #e3e3e3;
}

svg .position-fixed {
  position: fixed;
}

svg a {
  position: fixed;
}

.cursor-grab {
  cursor: grab !important;
}

.cursor-grab:active {
  cursor: grabbing !important;
}

[contenteditable="true"].single-line {
  white-space: nowrap;
  overflow: hidden;
}
[contenteditable="true"].single-line br {
  display: none;
}
[contenteditable="true"].single-line * {
  display: inline;
  white-space: nowrap;
}
</style>
